import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { FormEvent, MouseEvent } from "react";
import { RoomEvent, ConnectionState, LocalVideoTrack } from "livekit-client";
import { useLiveKitRoom } from "../hooks/useLiveKitRoom";
import { ParticipantsGrid, type Participant } from "./ParticipantsGrid";
import { DeviceSettingsModal } from "./DeviceSettingsModal";
import VideoEffectsPicker from "./VideoEffectsPicker"; // [MEMEET-VFX]
import { applyVideoEffect, readVideoEffect, writeVideoEffect, type VideoEffect } from "./videoEffects";
import { ShortcutsPanel } from "./ShortcutsPanel";
import { useMeMeetPrefs, comboFromEvent, isTypingTarget, comboAllowedWhileTyping, toElectronAccelerator } from "../memeetPrefs";
import MeetingSummary from "./MeetingSummary";
import soundboardConfig from "../config/soundboard.json";
import soundApplauseMid from "../assets/sound-effects/applause-mid.mp3";
import soundApplauseSingle from "../assets/sound-effects/applause-single.wav";
import soundMassApplause from "../assets/sound-effects/mass-applause.mp3";
import soundCrowdLaugh from "../assets/sound-effects/crowd-laugh.mp3";
import soundMockingLaugh from "../assets/sound-effects/mocking-laugh.wav";
import soundBreakingNews from "../assets/sound-effects/breaking-news.mp3";
import soundCricketChirp from "../assets/sound-effects/cricket-chirp.wav";
import soundCrowdAhh from "../assets/sound-effects/crowd-ahh.wav";
import soundHeavenlyChoir from "../assets/sound-effects/heavenly-choir-singing.mp3";
import soundManAhh from "../assets/sound-effects/man-ahh.wav";
import soundSadTrombone from "../assets/sound-effects/sad-trombone.mp3";
import soundWow from "../assets/sound-effects/wow.wav";
import "./VideoCall.css";

const soundFiles: Record<string, string> = {
  "applause-mid.mp3": soundApplauseMid,
  "applause-single.wav": soundApplauseSingle,
  "mass-applause.mp3": soundMassApplause,
  "crowd-laugh.mp3": soundCrowdLaugh,
  "mocking-laugh.wav": soundMockingLaugh,
  "breaking-news.mp3": soundBreakingNews,
  "cricket-chirp.wav": soundCricketChirp,
  "crowd-ahh.wav": soundCrowdAhh,
  "heavenly-choir-singing.mp3": soundHeavenlyChoir,
  "man-ahh.wav": soundManAhh,
  "sad-trombone.mp3": soundSadTrombone,
  "wow.wav": soundWow,
};

interface SoundboardItem {
  id: string;
  title: string;
  icon: string;
  file: string;
}

interface SoundboardWireMessageV1 {
  v: 1;
  type: "soundboard-play";
  soundId: string;
  fromName: string;
  ts: number;
}

type CallStatus = "idle" | "connecting" | "in-call" | "ended";

interface RemoteParticipant {
  id: string;
  displayName: string;
  avatarUrl?: string;
  lodosUserId?: string;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  screenShareMid?: string | null;
  isHandRaised: boolean;
}

interface ChatAttachment {
  type: string;
  name: string;
  data: string;
  size: number;
}

interface ChatMessage {
  id: string;
  text: string;
  ts: number;
  fromId: string;
  fromName: string;
  fromAvatarUrl?: string;
  isLocal: boolean;
  attachment?: ChatAttachment;
}

interface WorkspaceOption {
  id: string;
  title: string;
}

interface CollaboratorOption {
  id: string;
  name: string;
  email?: string;
}

interface MeMeetInvitation {
  invitationCode: string;
  userID?: string;
  email?: string;
  nameLastname?: string;
}

interface AddChatMessageToChecklistArgs {
  workspaceId: string;
  meetingId: string;
  meetingStartedAt: number;
  message: Pick<ChatMessage, "id" | "text" | "ts" | "fromId" | "fromName">;
}

interface AddChatMessageToChecklistResult {
  checklistId?: string;
  checklistName: string;
}

interface AddChatMessageToNotesArgs {
  meetingId: string;
  meetingStartedAt: number;
  message: Pick<ChatMessage, "id" | "text" | "ts" | "fromId" | "fromName">;
}

interface AddChatMessageToNotesResult {
  noteId?: string;
  noteTitle?: string;
}

interface ChatWireMessageV1 {
  v: 1;
  type: "chat-message";
  id: string;
  ts: number;
  text: string;
  from: {
    name: string;
    avatarUrl?: string;
  };
  attachment?: ChatAttachment;
  meta?: {
    kind: "checklist-added";
    messageId: string;
    workspaceId: string;
    checklistName: string;
    checklistId?: string;
    byUserId?: string;
    byName?: string;
    preview?: string;
  };
}

interface ChatWireChecklistAddedV1 {
  v: 1;
  type: "chat-checklist-added";
  ts: number;
  messageId: string;
  workspaceId: string;
  checklistName: string;
  checklistId?: string;
  byUserId?: string;
  byName?: string;
  preview?: string;
}

type ChatChecklistAddMeta = {
  workspaceId: string;
  checklistId?: string;
  checklistName: string;
  byUserId?: string;
  byName?: string;
  addedAt: number;
  preview?: string;
};

type ChatNotesAddMeta = {
  noteId?: string;
  noteTitle?: string;
  addedAt: number;
};

interface VideoCallProps {
  roomId: string;
  token?: string;
  lodosUserId?: string;
  nickname?: string;
  displayName?: string;
  avatarUrl?: string;
  language?: string;
  changeLanguage?: (lang: string) => void;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
  getShareUrl?: (roomId: string) => string;
  summaryApi?: any;
  getLinkPreview?: (token: string, url: string) => Promise<any>;
  onSendDmOnSocialHive?: (targetUserId: string, displayName: string) => void | Promise<void>;
  workspaces?: WorkspaceOption[];
  onAddChatMessageToChecklist?: (args: AddChatMessageToChecklistArgs) => Promise<AddChatMessageToChecklistResult | undefined>;
  onAddChatMessageToNotes?: (args: AddChatMessageToNotesArgs) => Promise<AddChatMessageToNotesResult | undefined>;
  onGetCollaboratorsService?: () => Promise<any>;
  onInviteUserService?: (options: {
    roomID: string;
    userID?: string;
    email?: string;
    nameLastname?: string;
    // Hangi kanallardan haber verilecegi KULLANICI secimidir.
    // Bos dizi = hicbir sey gonderme (kullanici linki kendi paylasir).
    notify?: string[];
  }) => Promise<any>;
  onIsInvitedUserService?: (options: {
    roomID: string;
    userID?: string;
    email?: string;
  }) => Promise<any>;
  onRemoveInvitationService?: (options: { invitationCode: string }) => Promise<any>;
  onGetInvitationsService?: (options: { roomID: string }) => Promise<any>;
  creatorUserId?: string;
  onCallEnd?: () => void;
  onExitRoom?: () => void;
  liveKitTokenEndpoint?: string;
  /** Odaya bağlanmadan önce içerideki katılımcıları getirir (pre-join presence) */
  onGetRoomParticipants?: (
    roomID: string
  ) => Promise<Array<{ identity: string; name?: string; joinedAt?: number }>>;
  /** Host (oda sahibi) bir katılımcının mikrofonunu zorla kapatır — owner-gated
   *  (flow/memeet-mute-participant). true dönerse başarılı. */
  onMuteParticipant?: (options: {
    roomID: string;
    identity: string;
    muted: boolean;
  }) => Promise<boolean>;
  /** Host (oda sahibi) tum toplantiyi video olarak kaydetmeye baslar — owner-gated
   *  (flow/memeet-start-video-recording). */
  onStartVideoRecording?: (options: { roomID: string }) => Promise<boolean>;
  /** Host video kaydini durdurur (flow/memeet-stop-video-recording). */
  onStopVideoRecording?: (options: { roomID: string }) => Promise<boolean>;
  /** Video kayit durumunu getirir (flow/memeet-video-recording-state) — riza gostergesi
   *  icin herkes cagirabilir; true ise "Kayit ediliyor" gostergesi gorunur. */
  onGetVideoRecordingState?: (options: { roomID: string }) => Promise<boolean>;
}

/* ── Tepkiler (2026-09-16) ───────────────────────────────────────────────────
 * Toplanti sirasinda anlik jest: alttan yukari suzulup soner, altinda
 * gonderenin adi yazar. Sohbete yazilmaz, kalici degildir.
 *
 * IKONLAR EMOJI DEGIL: emoji platformdan platforma farkli cizilir ve tema
 * rengini almaz. Hepsi inline stroke SVG, currentColor ile temaya uyar.
 */
type ReactionKind = "thumbsup" | "heart" | "applause" | "smile" | "thumbsdown";

interface FloatingReaction {
  id: string;
  kind: ReactionKind;
  fromName: string;
  /** Yatay sapma (px) — ayni anda gelen tepkiler ust uste binmesin. */
  offset: number;
}

/** Animasyon suresi; katmandan dusme zamanlayicisi bununla ayarli. */
const REACTION_LIFETIME_MS = 2900;
/** Yerel kullanici icin en kisa gonderim araligi (spam koruması). */
const REACTION_MIN_GAP_MS = 400;
/** Ayni anda ekranda duran en fazla tepki — kalabalik odada kare dusmesin. */
const REACTION_MAX_ON_SCREEN = 24;

const REACTION_KINDS: { kind: ReactionKind; labelKey: string; labelFallback: string }[] = [
  { kind: "thumbsup", labelKey: "memeetReactionThumbsUp", labelFallback: "Beğendim" },
  { kind: "heart", labelKey: "memeetReactionHeart", labelFallback: "Sevdim" },
  { kind: "applause", labelKey: "memeetReactionApplause", labelFallback: "Alkış" },
  { kind: "smile", labelKey: "memeetReactionSmile", labelFallback: "Güldüm" },
  { kind: "thumbsdown", labelKey: "memeetReactionThumbsDown", labelFallback: "Beğenmedim" },
];

function ReactionIcon({ kind }: { kind: ReactionKind }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (kind === "heart") {
    return (
      <svg {...common}>
        <path d="M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 1 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z" />
      </svg>
    );
  }
  if (kind === "applause") {
    // Alkis/harika — parlama yildizlari (el cizimi 20px'te okunmuyor).
    return (
      <svg {...common}>
        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
        <path d="M18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z" />
        <path d="M5 14l.6 1.6L7 16l-1.4.4L5 18l-.6-1.6L3 16l1.4-.4L5 14z" />
      </svg>
    );
  }
  if (kind === "smile") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
        <line x1="9" y1="9.5" x2="9.01" y2="9.5" />
        <line x1="15" y1="9.5" x2="15.01" y2="9.5" />
      </svg>
    );
  }
  if (kind === "thumbsdown") {
    return (
      <svg {...common}>
        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.7a2 2 0 0 0-2 1.7l-1.4 9A2 2 0 0 0 4.3 15H10z" />
        <path d="M17 2h2.7A2.3 2.3 0 0 1 22 4.3v6.4a2.3 2.3 0 0 1-2.3 2.3H17" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.3a2 2 0 0 0 2-1.7l1.4-9A2 2 0 0 0 19.7 9H14z" />
      <path d="M7 22H4.3A2.3 2.3 0 0 1 2 19.7v-6.4A2.3 2.3 0 0 1 4.3 11H7" />
    </svg>
  );
}

const MAX_CHAT_MESSAGES = 200;
const MAX_PREJOIN_PARTICIPANTS = 3;
const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024; // 2MB
const DEBUG_STORAGE_KEY = "memeet:debug";
const DEBUG_QUERY_KEY = "memeetDebug";
const DEBUG_PREFIX = "[MeMeetDebug]";
const HAND_AUTO_LOWER_SPEAKING_THRESHOLD = 6;
const HAND_AUTO_LOWER_DELAY_MS = 2000;
const HAND_AUTO_LOWER_SILENCE_GRACE_MS = 450;

type NotificationTone = "chat" | "join";

// Device selection ("Ayarlar") — kullanıcı tercihi localStorage'da saklanır ve
// prejoin getUserMedia + connect() default'u olarak kullanılır.
const PREF_AUDIO_IN = "memeetPreferredAudioIn";
const PREF_AUDIO_OUT = "memeetPreferredAudioOut";
const PREF_VIDEO_IN = "memeetPreferredVideoIn";

function readDevicePref(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    const value = window.localStorage.getItem(key);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

function writeDevicePref(key: string, value: string) {
  try {
    if (typeof window === "undefined") return;
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

interface PreConnectParticipant {
  id: string;
  displayName: string;
  avatarUrl?: string;
  lodosUserId?: string;
}

function isMeMeetDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const stored = String(window.localStorage.getItem(DEBUG_STORAGE_KEY) || "").trim().toLowerCase();
    if (stored === "1" || stored === "true" || stored === "on" || stored === "yes") return true;

    const params = new URLSearchParams(window.location.search);
    return params.get(DEBUG_QUERY_KEY) === "1";
  } catch {
    return false;
  }
}

function debugLog(event: string, payload?: Record<string, unknown>) {
  if (!isMeMeetDebugEnabled()) return;
  if (payload) {
    console.log(DEBUG_PREFIX, event, payload);
    return;
  }
  console.log(DEBUG_PREFIX, event);
}

type LinkPreviewState =
  | { title: string; description: string; image: string | null; url: string }
  | { error: true };

const URL_REGEX = /(https?:\/\/[^\s]+|(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;
const URL_REGEX_SINGLE = new RegExp(URL_REGEX.source, "i");

// Keep aligned with SocialHiveChat link detection to avoid false positives.
const VALID_TLDS = [
  ".test",
  ".example",
  ".invalid",
  ".localhost",
  ".dev",
  ".local",
  ".internal",
  ".com",
  ".net",
  ".org",
  ".edu",
  ".gov",
  ".mil",
  ".int",
  ".info",
  ".biz",
  ".name",
  ".pro",
  ".coop",
  ".aero",
  ".museum",
  ".app",
  ".io",
  ".ai",
  ".tech",
  ".online",
  ".site",
  ".website",
  ".store",
  ".shop",
  ".blog",
  ".news",
  ".media",
  ".agency",
  ".company",
  ".digital",
  ".email",
  ".cloud",
  ".network",
  ".systems",
  ".technology",
  ".software",
  ".computer",
  ".codes",
  ".design",
  ".graphics",
  ".photo",
  ".photography",
  ".video",
  ".audio",
  ".music",
  ".film",
  ".studio",
  ".art",
  ".gallery",
  ".zone",
  ".space",
  ".world",
  ".earth",
  ".live",
  ".life",
  ".community",
  ".social",
  ".chat",
  ".link",
  ".click",
  ".wiki",
  ".academy",
  ".university",
  ".school",
  ".education",
  ".institute",
  ".college",
  ".work",
  ".career",
  ".jobs",
  ".services",
  ".solutions",
  ".support",
  ".help",
  ".guide",
  ".tips",
  ".tools",
  ".today",
  ".now",
  ".here",
  ".pub",
  ".cafe",
  ".bar",
  ".restaurant",
  ".pizza",
  ".food",
  ".recipes",
  ".cooking",
  ".kitchen",
  ".menu",
  ".fitness",
  ".health",
  ".medical",
  ".care",
  ".doctor",
  ".dental",
  ".surgery",
  ".clinic",
  ".hospital",
  ".money",
  ".finance",
  ".financial",
  ".bank",
  ".credit",
  ".loan",
  ".tax",
  ".accountant",
  ".trading",
  ".insurance",
  ".fund",
  ".ventures",
  ".capital",
  ".estate",
  ".homes",
  ".house",
  ".property",
  ".realty",
  ".land",
  ".casa",
  ".travel",
  ".flights",
  ".hotels",
  ".vacations",
  ".tours",
  ".cruise",
  ".auto",
  ".car",
  ".cars",
  ".taxi",
  ".bike",
  ".motorcycles",
  ".game",
  ".games",
  ".poker",
  ".casino",
  ".bet",
  ".tennis",
  ".golf",
  ".football",
  ".soccer",
  ".hockey",
  ".baseball",
  ".basketball",
  ".racing",
  ".team",
  ".coach",
  ".fans",
  ".fashion",
  ".clothing",
  ".shoes",
  ".jewelry",
  ".watch",
  ".boutique",
  ".style",
  ".beauty",
  ".makeup",
  ".hair",
  ".skin",
  ".tattoo",
  ".ink",
  ".gifts",
  ".flowers",
  ".toys",
  ".baby",
  ".kids",
  ".family",
  ".wedding",
  ".events",
  ".party",
  ".christmas",
  ".holiday",
  ".us",
  ".uk",
  ".ca",
  ".au",
  ".de",
  ".fr",
  ".it",
  ".es",
  ".nl",
  ".be",
  ".ch",
  ".at",
  ".se",
  ".no",
  ".dk",
  ".fi",
  ".pl",
  ".cz",
  ".ru",
  ".ua",
  ".jp",
  ".cn",
  ".kr",
  ".in",
  ".sg",
  ".hk",
  ".tw",
  ".th",
  ".my",
  ".id",
  ".ph",
  ".vn",
  ".nz",
  ".za",
  ".br",
  ".mx",
  ".ar",
  ".cl",
  ".co",
  ".pe",
  ".ve",
  ".tr",
  ".il",
  ".ae",
  ".sa",
  ".eg",
  ".ng",
  ".ke",
  ".gh",
  ".ma",
  ".tn",
  ".tv",
  ".me",
  ".cc",
  ".ws",
  ".to",
  ".fm",
  ".am",
  ".ly",
  ".gl",
  ".im",
  ".ac",
  ".sh",
  ".tm",
  ".vc",
  ".ag",
  ".bz",
  ".mn",
  ".nu",
  ".sc",
  ".tk",
  ".ga",
  ".cf",
  ".gq",
  ".ml",
];

function hasValidTld(url: string) {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return VALID_TLDS.some((tld) => hostname.endsWith(tld));
  } catch {
    return false;
  }
}

function normalizeUrl(raw: string): string | null {
  if (!raw) return null;
  let u = String(raw).trim();
  u = u.replace(/[),.;!?]+$/, "");
  if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
  try {
    const url = new URL(u);
    if (!hasValidTld(u)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function extractUrls(text: string): string[] {
  if (!text || typeof text !== "string") return [];
  const textWithoutEmails = text.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "");
  const found = textWithoutEmails.match(URL_REGEX) || [];
  const normalized = found.map((v) => normalizeUrl(v)).filter(Boolean) as string[];
  return Array.from(new Set(normalized));
}

function getHostName(href: string): string {
  if (!href) return "";
  try {
    const urlObj = new URL(href);
    return urlObj.host.replace(/^www\./i, "") || urlObj.host || href;
  } catch {
    return href;
  }
}

function getResponseData(response: any): any {
  if (!response) return null;
  if (Object.prototype.hasOwnProperty.call(response, "data")) return response.data;
  return response;
}

function readResponseStatus(response: any): boolean {
  const data = getResponseData(response);
  if (typeof data === "boolean") return data;
  if (typeof data?.status === "boolean") return data.status;
  if (typeof response?.status === "boolean") return response.status;
  return false;
}

function normalizeCollaboratorsResponse(response: any): CollaboratorOption[] {
  const data = getResponseData(response);
  if (data && typeof data === "object" && data.status === false) return [];

  const pickArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object") return [];

    const keys = ["result", "collaborators", "members", "items", "rows", "list"];
    for (const key of keys) {
      const next = value[key];
      if (Array.isArray(next)) return next;
      if (next && typeof next === "object") {
        for (const nestedKey of ["collaborators", "members", "items", "rows", "list"]) {
          if (Array.isArray(next[nestedKey])) return next[nestedKey];
        }
      }
    }
    return [];
  };

  const result = pickArray(data);
  const seen = new Set<string>();

  return result
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const rawId = String(
        entry.memberID ??
          entry.targetUserId ??
          entry.collaboratorID ??
          entry.userID ??
          entry.id ??
          ""
      ).trim();
      const email = String(entry.email ?? entry.invitedEmail ?? "").trim() || undefined;
      const name =
        String(
          entry.nameLastname ??
            [entry.name, entry.lastname].filter(Boolean).join(" ") ??
            entry.displayName ??
            entry.fullName ??
            entry.username ??
            entry.email ??
            rawId
        ).trim() || rawId;

      if (!rawId && !email) return null;
      const dedupeKey = rawId || `email:${email}`;
      if (!dedupeKey || seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      return {
        id: rawId || `email:${email}`,
        name,
        email,
      } as CollaboratorOption;
    })
    .filter((item: CollaboratorOption | null): item is CollaboratorOption => Boolean(item));
}

function normalizeInvitationsResponse(response: any): MeMeetInvitation[] {
  const data = getResponseData(response);
  const result = Array.isArray(data?.result) ? data.result : Array.isArray(data) ? data : [];
  const seen = new Set<string>();

  return result
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const invitationCode = String(
        entry.invitationCode ??
          entry.invitation_code ??
          entry.code ??
          ""
      ).trim();
      if (!invitationCode || seen.has(invitationCode)) return null;
      seen.add(invitationCode);

      const userID = String(
        entry.userID ??
          entry.invitedUserID ??
          entry.memberID ??
          ""
      ).trim() || undefined;
      const email = String(entry.email ?? entry.invitedEmail ?? "").trim() || undefined;
      const nameLastname =
        String(
          entry.nameLastname ??
            [entry.name, entry.lastname].filter(Boolean).join(" ") ??
            [entry.invitedName, entry.invitedLastname].filter(Boolean).join(" ") ??
            ""
        ).trim() || undefined;

      return {
        invitationCode,
        userID,
        email,
        nameLastname,
      } as MeMeetInvitation;
    })
    .filter((item: MeMeetInvitation | null): item is MeMeetInvitation => Boolean(item));
}

function makeInvitedCheckKey(prefix: string, userID?: string, email?: string): string {
  const normalizedUserID = String(userID || "").trim();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (normalizedUserID) return `${prefix}:u:${normalizedUserID}`;
  if (normalizedEmail) return `${prefix}:e:${normalizedEmail}`;
  return `${prefix}:unknown`;
}

function displayInvitationName(invitation: MeMeetInvitation): string {
  const explicit = String(invitation.nameLastname || "").trim();
  if (explicit) return explicit;
  const fallback = String(invitation.email || invitation.userID || "").trim();
  return fallback || "Unknown";
}

function linkifyText(text: string) {
  const parts = String(text || "").split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    if (URL_REGEX_SINGLE.test(part)) {
      const href = normalizeUrl(part);
      if (!href) return <span key={i}>{part}</span>;
      return (
        <a
          key={`${i}-${href}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            try {
              const evt = new CustomEvent("lodos-browser:open-url", {
                detail: { url: href },
                bubbles: true,
                cancelable: true,
              });
              const prevented = !window.dispatchEvent(evt);
              if (prevented) {
                e.preventDefault();
              }
            } catch {
              // fallback: let default <a> behavior handle it
            }
          }}
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const PREVIEW_PLACEHOLDER_DATA_URI =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" viewBox="0 0 800 450">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#1f2937" stop-opacity="0.55"/>
          <stop offset="1" stop-color="#2563eb" stop-opacity="0.35"/>
        </linearGradient>
      </defs>
      <rect width="800" height="450" fill="url(#g)"/>
      <circle cx="640" cy="120" r="90" fill="#111827" fill-opacity="0.25"/>
      <rect x="80" y="290" width="520" height="26" rx="13" fill="#ffffff" fill-opacity="0.22"/>
      <rect x="80" y="330" width="380" height="20" rx="10" fill="#ffffff" fill-opacity="0.16"/>
    </svg>`
  );

function LinkPreviewSkeleton() {
  return (
    <div className="link-preview-card wa is-loading" aria-hidden="true">
      <div className="wa-thumb">
        <div className="wa-thumb__skeleton" />
      </div>
      <div className="wa-meta">
        <div className="wa-title" />
        <div className="wa-desc" />
        <div className="wa-host" />
      </div>
    </div>
  );
}

function LinkPreviewCard({
  preview,
  href,
  getTranslation,
}: {
  preview: { title: string; description: string; image: string | null; url: string };
  href: string;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
}) {
  const host = getHostName(href);
  const urlObj = (() => {
    try {
      return new URL(href);
    } catch {
      return null;
    }
  })();
  const normalizedTitle = preview?.title?.trim() || href;
  const displayImage = (() => {
    const raw = preview?.image ? String(preview.image) : "";
    if (!raw) return PREVIEW_PLACEHOLDER_DATA_URI;
    if (raw.startsWith("//")) return `https:${raw}`;
    if (/^https?:\/\//i.test(raw)) return raw;
    return normalizeUrl(raw) || raw || PREVIEW_PLACEHOLDER_DATA_URI;
  })();
  const isPlaceholder = !preview?.image;
  const hasDescription = !!(preview?.description && preview.description.trim());
  const hasTitle = !!preview?.title?.trim();
  const defaultDescription = getTranslation ? getTranslation("clickToLearnMore") : "Click to learn more";

  const shouldShowTitleOverlay = hasTitle && hasDescription && isPlaceholder;
  const shouldShowHostOverlay = isPlaceholder && !shouldShowTitleOverlay;

  const origin = urlObj ? `${urlObj.protocol}//${urlObj.host}` : null;
  const primaryFavicon = origin ? `${origin}/favicon.ico` : "";

  return (
    <a className="link-preview-card wa" href={href} target="_blank" rel="noopener noreferrer" title={normalizedTitle}>
      <div className={`wa-thumb ${isPlaceholder ? "wa-thumb--placeholder" : ""}`}>
        <img
          className="wa-thumb__img"
          src={displayImage}
          alt={normalizedTitle}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== PREVIEW_PLACEHOLDER_DATA_URI) {
              e.currentTarget.src = PREVIEW_PLACEHOLDER_DATA_URI;
            }
          }}
        />
        {shouldShowHostOverlay && <div className="wa-thumb__placeholder-label">{host}</div>}
        {shouldShowTitleOverlay && (
          <div className="wa-thumb__placeholder-label wa-thumb__placeholder-label--on-image wa-thumb__placeholder-label--title">
            <span>{preview.title}</span>
          </div>
        )}
      </div>

      <div className="wa-meta">
        <div className="wa-title" title={normalizedTitle}>
          {normalizedTitle}
        </div>
        <div className="wa-desc">{hasDescription ? preview.description : defaultDescription}</div>
        <div className="wa-host">
          {primaryFavicon ? <img className="wa-favicon" src={primaryFavicon} alt="" loading="lazy" /> : null}
          <span className="wa-host__text">{host}</span>
        </div>
      </div>
    </a>
  );
}

export function LiveKitVideoCall({
  roomId,
  token,
  lodosUserId,
  nickname = "User",
  displayName,
  avatarUrl,
  language,
  changeLanguage: _changeLanguage,
  getTranslation,
  getShareUrl,
  summaryApi,
  getLinkPreview,
  onSendDmOnSocialHive,
  workspaces,
  onAddChatMessageToChecklist,
  onAddChatMessageToNotes,
  onGetCollaboratorsService,
  onInviteUserService,
  onIsInvitedUserService,
  onRemoveInvitationService,
  onGetInvitationsService,
  creatorUserId,
  onCallEnd,
  onExitRoom,
  liveKitTokenEndpoint,
  onGetRoomParticipants,
  onMuteParticipant,
  onStartVideoRecording,
  onStopVideoRecording,
  onGetVideoRecordingState,
}: VideoCallProps) {
  // -------------------------------------------------------------------------
  // UI / state — same shape as the previous WebRTC mesh implementation
  // -------------------------------------------------------------------------
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [handRaisedByIdentity, setHandRaisedByIdentity] = useState<Map<string, boolean>>(new Map());
  // Ref mirror — DataReceived closure'unda güncel raised durumunu okuyup
  // yalnız false→true geçişinde çın çalmak için (tekrar eden raised mesajında değil).
  const handRaisedByIdentityRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => { handRaisedByIdentityRef.current = handRaisedByIdentity; }, [handRaisedByIdentity]);
  // [MEMEET-HANDORDER] 2026-11-05: elin KALKMA zamani (gonderenin ts'i) — sira icin. Tum katilimcilar
  // ayni ts'i gordugu icin sira herkeste aynidir; yeniden yayinda ilk kalkma zamani korunur.
  const [handRaisedAtByIdentity, setHandRaisedAtByIdentity] = useState<Map<string, number>>(new Map());
  const localHandRaisedAtRef = useRef<number | null>(null);
  const [localHandRaisedAt, setLocalHandRaisedAt] = useState<number | null>(null);
  // ── Tepkiler (2026-09-16) ────────────────────────────────────────────────
  // Ekranda suzulen tepkiler. Kalici degil: animasyon bitince listeden dusuyor,
  // sohbete de yazilmiyor — anlik bir jest.
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const reactionTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const reactionSeqRef = useRef(0);
  // Spam koruması: yerel kullanici en fazla REACTION_MIN_GAP_MS'de bir gonderir.
  const lastReactionAtRef = useRef(0);

  const handChimeCtxRef = useRef<AudioContext | null>(null);
  // El kaldırma bildirim sesi — Web Audio ile nazik iki tonlu "çın" (dosya yok).
  const playHandRaiseChime = useCallback(() => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      let ctx = handChimeCtxRef.current;
      if (!ctx) { ctx = new Ctx(); handChimeCtxRef.current = ctx; }
      if (ctx!.state === "suspended") void ctx!.resume();
      const now = ctx!.currentTime;
      ([[880, 0], [1175, 0.12]] as [number, number][]).forEach(([freq, offset]) => {
        const osc = ctx!.createOscillator();
        const gain = ctx!.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const t0 = now + offset;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
        osc.connect(gain).connect(ctx!.destination);
        osc.start(t0);
        osc.stop(t0 + 0.24);
      });
    } catch { /* sessiz */ }
  }, []);
  const [callDuration, setCallDuration] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isSoundboardOpen, setIsSoundboardOpen] = useState(false);
  const [playingSoundId, setPlayingSoundId] = useState<string | null>(null);
  const soundboardAudioRef = useRef<HTMLAudioElement | null>(null);
  const [chatDraft, setChatDraft] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState<ChatAttachment | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [linkPreviews, setLinkPreviews] = useState<Record<string, LinkPreviewState>>({});
  const fetchingUrlsRef = useRef<Set<string>>(new Set());
  const meetingStartedAtRef = useRef<number>(Date.now());

  const [chatContextMenu, setChatContextMenu] = useState<{
    messageId: string;
    x: number;
    y: number;
  } | null>(null);

  const [isChecklistPickerOpen, setIsChecklistPickerOpen] = useState(false);
  const [checklistPickerMessageId, setChecklistPickerMessageId] = useState<string | null>(null);
  const [hasChecklistWorkspaceSelection, setHasChecklistWorkspaceSelection] = useState<boolean>(() => {
    try {
      if (typeof window === "undefined") return false;
      return window.localStorage.getItem("memeet:hasChecklistWorkspaceSelection") === "1";
    } catch {
      return false;
    }
  });
  const [selectedChecklistWorkspaceId, setSelectedChecklistWorkspaceId] = useState<string>(() => {
    try {
      if (typeof window === "undefined") return "";
      return String(window.localStorage.getItem("memeet:lastChecklistWorkspaceId") || "");
    } catch {
      return "";
    }
  });
  const [checklistActionError, setChecklistActionError] = useState("");
  const [addingChecklistForMessageId, setAddingChecklistForMessageId] = useState<string | null>(null);
  const [isAddingToNotesForMessageId, setIsAddingToNotesForMessageId] = useState<string | null>(null);

  const [checklistAddsByMessageId, setChecklistAddsByMessageId] = useState<Record<string, ChatChecklistAddMeta[]>>({});
  const [notesAddedByMessageId, setNotesAddedByMessageId] = useState<Record<string, ChatNotesAddMeta>>({});

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  // Davet bildirimi kanallari. UCU DE varsayilan acik (2026-10-01): mail kapali
  // baslayinca kullanicilar kutuyu fark etmiyor, "davet maili gitmedi" sikayeti
  // geliyordu (21 davetten 1 mail). Hicbiri secilmezse backend hicbir sey
  // gondermez — davet yine olusur, link kopyalanabilir.
  const [notifyChannels, setNotifyChannels] = useState<{ notification: boolean; push: boolean; mail: boolean }>({
    notification: true,
    push: true,
    mail: true,
  });
  // Davet olustuktan sonra donen katilim baglantisi ("kopyala ve kendim gonder").
  const [inviteJoinLink, setInviteJoinLink] = useState("");
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
  const [inviteActionError, setInviteActionError] = useState("");
  const [inviteActionSuccess, setInviteActionSuccess] = useState("");
  const [collaborators, setCollaborators] = useState<CollaboratorOption[]>([]);
  const [isCollaboratorsLoading, setIsCollaboratorsLoading] = useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState("");
  const [collaboratorSearchQuery, setCollaboratorSearchQuery] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNameLastname, setInviteNameLastname] = useState("");
  const [invitations, setInvitations] = useState<MeMeetInvitation[]>([]);
  const [isInvitationsLoading, setIsInvitationsLoading] = useState(false);
  const [invitedStatusByKey, setInvitedStatusByKey] = useState<Record<string, boolean>>({});
  const [isCheckingInvitedByKey, setIsCheckingInvitedByKey] = useState<Record<string, boolean>>({});
  const [invitationToRemove, setInvitationToRemove] = useState<MeMeetInvitation | null>(null);
  const [isRemovingInvitation, setIsRemovingInvitation] = useState(false);

  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  // Track-ID bazlı MediaStream cache: konuşmacı değiştiğinde (ActiveSpeakersChanged)
  // hook participants array'ini yeniler; track'ler aynıyken her seferinde yeni
  // MediaStream yaratırsak <video>.srcObject re-attach olur ve flicker yaşanır.
  // Bu cache ile aynı track ID seti varsa eski stream referansı korunur.
  const streamCacheRef = useRef<Map<string, { key: string; stream: MediaStream }>>(new Map());
  const getCachedStream = useCallback((cacheKey: string, tracks: MediaStreamTrack[]): MediaStream => {
    const trackKey = tracks.map((t) => t.id).sort().join("|");
    const hit = streamCacheRef.current.get(cacheKey);
    if (hit && hit.key === trackKey) return hit.stream;
    const stream = new MediaStream(tracks);
    streamCacheRef.current.set(cacheKey, { key: trackKey, stream });
    return stream;
  }, []);
  const localHandRaisedRef = useRef(false);
  const chatMessageIdsRef = useRef<Set<string>>(new Set());
  const chatOpenRef = useRef(false);
  const chatListRef = useRef<HTMLDivElement>(null);
  const chatDraftInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shouldAutoScrollChatRef = useRef(true);
  const hasLoggedDebugSessionRef = useRef(false);
  const autoLowerHandTimeoutRef = useRef<number | null>(null);
  const autoLowerHandSilenceTimeoutRef = useRef<number | null>(null);
  const notificationAudioContextRef = useRef<AudioContext | null>(null);

  // -------------------------------------------------------------------------
  // LiveKit transport
  // -------------------------------------------------------------------------
  const lk = useLiveKitRoom({
    tokenEndpoint: liveKitTokenEndpoint || "https://prod.apiollon.com/flow/memeet-livekit-token",
    authToken: token || undefined,
    videoPreset: "h720",
    onError: (e) => console.error("[LiveKit]", e),
  });

  // Derive a MediaStream from local LiveKit tracks so downstream UI (preview
  // video element, gridParticipants local tile) can keep its existing API.
  const localStream = useMemo(() => {
    const tracks: MediaStreamTrack[] = [];
    if (lk.localParticipant?.audioTrack) tracks.push(lk.localParticipant.audioTrack);
    if (lk.localParticipant?.videoTrack) tracks.push(lk.localParticipant.videoTrack);
    return tracks.length ? getCachedStream("local", tracks) : null;
  }, [lk.localParticipant?.audioTrack, lk.localParticipant?.videoTrack]);

  // Prejoin (lobby) state'leri — kullanıcı LiveKit'e bağlanmadan önce mic/cam tercihi.
  const [prejoinAudioEnabled, setPrejoinAudioEnabled] = useState(true);
  const [prejoinVideoEnabled, setPrejoinVideoEnabled] = useState(true);
  const [prejoinStream, setPrejoinStream] = useState<MediaStream | null>(null);
  const prejoinStreamRef = useRef<MediaStream | null>(null);
  // [MEMEET-VFX] 2026-11-05: arka plan efekti (bulaniklik / gorsel). Girisoncesi secilir, toplantida da gecerli,
  // cihazda saklanir. prejoinPreview = on izlemede gosterilen ISLENMIS goruntu (efekt kapaliyken null).
  const [videoEffect, setVideoEffectState] = useState<VideoEffect>(() => readVideoEffect());
  const [vfxBusy, setVfxBusy] = useState(false);
  const [prejoinPreview, setPrejoinPreview] = useState<MediaStream | null>(null);
  // Kullanıcıya GÖRÜNÜR hata/uyarı (2026-10-01). Eskiden token, bağlantı ve izin
  // hataları yalnız konsola yazılıyor, kullanıcı siyah ekrana bakıyordu.
  const [joinError, setJoinError] = useState<string>("");
  const [prejoinNotice, setPrejoinNotice] = useState<string>("");
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);

  // Pre-join "who's inside" — backend endpoint'ten çekilen canlı katılımcılar.
  const [preConnectParticipants, setPreConnectParticipants] = useState<PreConnectParticipant[]>([]);

  // Device selection ("Ayarlar") state'i — localStorage tercihleriyle başlar.
  const [selectedAudioInput, setSelectedAudioInput] = useState<string | null>(() => readDevicePref(PREF_AUDIO_IN));
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | null>(() => readDevicePref(PREF_AUDIO_OUT));

  // Kullanıcı tercihleri (2026-10-14): kısayollar, SoundPad sesi, katılımcı ses ayarları — hesapta saklanır,
  // masaüstü / web / standalone / mobil MeMeet'te aynı. Hoparlör kapalı (kimseyi duymama) yalnız bu görüşmede.
  const memeetPrefs = useMeMeetPrefs({
    token,
    apiBase: (liveKitTokenEndpoint || "https://prod.apiollon.com/flow/memeet-livekit-token").replace(/flow\/.*$/, ""),
  });
  const [isDeafened, setIsDeafened] = useState(false);
  const soundpadVolumeRef = useRef(memeetPrefs.prefs.soundpadVolume);
  soundpadVolumeRef.current = memeetPrefs.prefs.soundpadVolume;
  const deafenedRef = useRef(false);
  deafenedRef.current = isDeafened;
  const audioOutRef = useRef<string | null>(selectedAudioOutput);
  audioOutRef.current = selectedAudioOutput;
  const [selectedVideoInput, setSelectedVideoInput] = useState<string | null>(() => readDevicePref(PREF_VIDEO_IN));
  const [isDeviceSettingsOpen, setIsDeviceSettingsOpen] = useState(false);

  // Prejoin'da preview state'i, in-call'da LiveKit'in kendi state'i göster.
  const isAudioEnabled = callStatus === "idle" ? prejoinAudioEnabled : lk.isMicEnabled;
  const isVideoEnabled = callStatus === "idle" ? prejoinVideoEnabled : lk.isCameraEnabled;
  const isScreenSharing = lk.isScreenSharing;
  const audioLevel = lk.localParticipant?.speaking ? 10 : 0;

  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  const effectiveDisplayName = useMemo(
    () => displayName?.trim() || nickname?.trim() || "User",
    [displayName, nickname]
  );

  const localIdentity = lk.localParticipant?.identity || "";

  useEffect(() => {
    if (hasLoggedDebugSessionRef.current) return;
    if (!isMeMeetDebugEnabled()) return;

    debugLog("session-context", {
      roomId,
      tokenPresent: Boolean(token),
      lodosUserId: lodosUserId ? String(lodosUserId) : null,
      effectiveDisplayName,
      transport: "livekit",
    });
    hasLoggedDebugSessionRef.current = true;
  }, [roomId, token, lodosUserId, effectiveDisplayName]);

  const t = useCallback(
    (key: string, fallback: string, params?: Record<string, string>) => {
      if (!getTranslation) return fallback;
      const translated = getTranslation(key, params || {});
      if (!translated || translated === key) return fallback;
      return translated;
    },
    [getTranslation]
  );

  // -------------------------------------------------------------------------
  // Notification tone (synthesized) and soundboard playback
  // -------------------------------------------------------------------------
  const playNotificationTone = useCallback((tone: NotificationTone) => {
    try {
      if (typeof window === "undefined" || typeof window.AudioContext === "undefined") return;

      if (!notificationAudioContextRef.current || notificationAudioContextRef.current.state === "closed") {
        notificationAudioContextRef.current = new window.AudioContext();
      }

      const audioContext = notificationAudioContextRef.current;
      if (!audioContext) return;

      if (audioContext.state === "suspended") {
        void audioContext.resume().catch(() => undefined);
      }

      const notes =
        tone === "chat"
          ? [
              { frequency: 1046, duration: 0.06 },
              { frequency: 1318, duration: 0.06 },
            ]
          : [{ frequency: 880, duration: 0.09 }];

      let cursor = audioContext.currentTime + 0.01;
      notes.forEach((note) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(note.frequency, cursor);

        gainNode.gain.setValueAtTime(0.0001, cursor);
        gainNode.gain.exponentialRampToValueAtTime(0.12, cursor + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, cursor + note.duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(cursor);
        oscillator.stop(cursor + note.duration + 0.02);

        cursor += note.duration + 0.04;
      });
    } catch {
      // ignore
    }
  }, []);

  const playSoundboardSound = useCallback((soundId: string) => {
    const soundItem = (soundboardConfig.sounds as SoundboardItem[]).find((s) => s.id === soundId);
    if (!soundItem) return;

    const src = soundFiles[soundItem.file];
    if (!src) return;

    try {
      if (soundboardAudioRef.current) {
        soundboardAudioRef.current.pause();
        soundboardAudioRef.current.currentTime = 0;
      }

      const audio = new Audio(src);
      // SoundPad ses seviyesi (kendi + gelen efektler) ve hoparlör kapalıysa sessiz; seçili hoparlöre yönlendir
      audio.volume = deafenedRef.current ? 0 : Math.max(0, Math.min(1, soundpadVolumeRef.current));
      const audioAny = audio as any;
      if (audioOutRef.current && typeof audioAny.setSinkId === "function") {
        try { const p = audioAny.setSinkId(audioOutRef.current); if (p && typeof p.catch === "function") p.catch(() => undefined); } catch { /* yok */ }
      }
      soundboardAudioRef.current = audio;
      setPlayingSoundId(soundId);

      audio.addEventListener("ended", () => {
        setPlayingSoundId((prev) => (prev === soundId ? null : prev));
      });
      audio.addEventListener("error", () => {
        setPlayingSoundId((prev) => (prev === soundId ? null : prev));
      });

      void audio.play().catch(() => {
        setPlayingSoundId(null);
      });
    } catch {
      setPlayingSoundId(null);
    }
  }, []);

  const broadcastSoundboardPlay = useCallback((soundId: string) => {
    const wireMessage: SoundboardWireMessageV1 = {
      v: 1,
      type: "soundboard-play",
      soundId,
      fromName: effectiveDisplayName,
      ts: Date.now(),
    };
    void lk.sendData(JSON.stringify(wireMessage), true).catch(() => undefined);
  }, [effectiveDisplayName, lk]);

  const handleSoundboardClick = useCallback((soundId: string) => {
    playSoundboardSound(soundId);
    broadcastSoundboardPlay(soundId);
  }, [playSoundboardSound, broadcastSoundboardPlay]);

  const toggleSoundboard = useCallback(() => {
    setIsSoundboardOpen((prev) => !prev);
  }, []);

  // -------------------------------------------------------------------------
  // Workspace / collaborators / invitations (UNCHANGED — pure UI/service)
  // -------------------------------------------------------------------------
  const resolvedWorkspaces = useMemo(() => {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const normalized = list
      .map((w) => ({
        id: String(w?.id || "").trim(),
        title: String(w?.title || "").trim(),
      }))
      .filter((w) => w.id)
      .map((w) => ({ ...w, title: w.title || w.id }));

    const seen = new Set<string>();
    const deduped = normalized.filter((w) => {
      if (seen.has(w.id)) return false;
      seen.add(w.id);
      return true;
    });

    deduped.sort((a, b) => a.title.localeCompare(b.title));
    return deduped;
  }, [workspaces]);

  const workspaceIdSet = useMemo(() => new Set(resolvedWorkspaces.map((w) => String(w.id))), [resolvedWorkspaces]);

  const canAddToChecklist = resolvedWorkspaces.length > 0 && typeof onAddChatMessageToChecklist === "function";
  const canAddToNotes = typeof onAddChatMessageToNotes === "function";
  const isChecklistAddInFlight = Boolean(addingChecklistForMessageId);
  const isNotesAddInFlight = Boolean(isAddingToNotesForMessageId);
  const canInviteUsers = typeof onInviteUserService === "function";
  const canRemoveInvitations = typeof onRemoveInvitationService === "function";
  const canLoadInvitations = typeof onGetInvitationsService === "function";
  const isRoomCreator = Boolean(creatorUserId) && String(lodosUserId || "") === String(creatorUserId || "");
  const canLoadCollaborators = typeof onGetCollaboratorsService === "function";

  const normalizeCollaboratorSearchText = useCallback(
    (value: string) => String(value || "").toLocaleLowerCase().replace(/\s+/g, " ").trim(),
    []
  );
  const collaboratorSearchTerm = useMemo(
    () => normalizeCollaboratorSearchText(collaboratorSearchQuery),
    [collaboratorSearchQuery, normalizeCollaboratorSearchText]
  );
  const filteredCollaborators = useMemo(() => {
    if (!collaboratorSearchTerm) return collaborators;
    return collaborators.filter((collaborator) => {
      const searchableText = normalizeCollaboratorSearchText(
        `${String(collaborator.name || "")} ${String(collaborator.email || "")}`
      );
      return searchableText.includes(collaboratorSearchTerm);
    });
  }, [collaborators, collaboratorSearchTerm, normalizeCollaboratorSearchText]);

  const fetchCollaborators = useCallback(async (): Promise<CollaboratorOption[]> => {
    if (!onGetCollaboratorsService) {
      setCollaborators([]);
      return [];
    }

    setIsCollaboratorsLoading(true);
    try {
      const response = await onGetCollaboratorsService();
      const next = normalizeCollaboratorsResponse(response).filter(
        (item) => String(item.id || "").trim() !== String(lodosUserId || "").trim()
      );
      setCollaborators(next);
      return next;
    } catch {
      setCollaborators([]);
      return [];
    } finally {
      setIsCollaboratorsLoading(false);
    }
  }, [onGetCollaboratorsService, lodosUserId]);

  const fetchInvitations = useCallback(async (): Promise<MeMeetInvitation[]> => {
    if (!onGetInvitationsService || !roomId) {
      setInvitations([]);
      return [];
    }

    setIsInvitationsLoading(true);
    try {
      const response = await onGetInvitationsService({ roomID: roomId });
      const next = normalizeInvitationsResponse(response);
      setInvitations(next);
      return next;
    } catch {
      setInvitations([]);
      return [];
    } finally {
      setIsInvitationsLoading(false);
    }
  }, [onGetInvitationsService, roomId]);

  const checkInvitedForTarget = useCallback(
    async ({
      key,
      userID,
      email,
    }: {
      key: string;
      userID?: string;
      email?: string;
    }) => {
      const normalizedUserID = String(userID || "").trim();
      const normalizedEmail = String(email || "").trim();
      if (!onIsInvitedUserService || !roomId || (!normalizedUserID && !normalizedEmail)) return;

      setIsCheckingInvitedByKey((prev) => ({ ...prev, [key]: true }));
      try {
        const response = await onIsInvitedUserService({
          roomID: roomId,
          userID: normalizedUserID || undefined,
          email: normalizedEmail || undefined,
        });
        const isInvited = readResponseStatus(response);
        setInvitedStatusByKey((prev) => ({ ...prev, [key]: isInvited }));
      } catch {
        setInvitedStatusByKey((prev) => ({ ...prev, [key]: false }));
      } finally {
        setIsCheckingInvitedByKey((prev) => ({ ...prev, [key]: false }));
      }
    },
    [onIsInvitedUserService, roomId]
  );

  const refreshInvitedChecks = useCallback(
    async (nextCollaborators: CollaboratorOption[], nextInvitations: MeMeetInvitation[]) => {
      if (!onIsInvitedUserService || !roomId) {
        setInvitedStatusByKey({});
        setIsCheckingInvitedByKey({});
        return;
      }

      const checks: Promise<void>[] = [];

      nextCollaborators.forEach((item) => {
        const maybeSyntheticId = String(item.id || "");
        const userID = maybeSyntheticId.startsWith("email:") ? "" : maybeSyntheticId;
        const key = makeInvitedCheckKey("collab", userID, item.email);
        checks.push(checkInvitedForTarget({ key, userID, email: item.email }));
      });

      nextInvitations.forEach((item) => {
        const key = makeInvitedCheckKey("invite", item.userID, item.email);
        checks.push(checkInvitedForTarget({ key, userID: item.userID, email: item.email }));
      });

      if (checks.length > 0) {
        await Promise.all(checks.map((task) => task.catch(() => undefined)));
      }
    },
    [onIsInvitedUserService, roomId, checkInvitedForTarget]
  );

  const closeInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
    setInviteActionError("");
    setInviteActionSuccess("");
    setSelectedCollaboratorId("");
    setCollaboratorSearchQuery("");
    setInviteEmail("");
    setInviteNameLastname("");
    setInvitationToRemove(null);
  }, []);

  const openInviteModal = useCallback(() => {
    setInviteActionError("");
    setInviteActionSuccess("");
    setCollaboratorSearchQuery("");
    setIsInviteModalOpen(true);
  }, []);

  const handleInviteSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!onInviteUserService || !roomId) return;

      const normalizedSelectedCollaboratorId = String(selectedCollaboratorId || "").trim();
      const normalizedEmail = String(inviteEmail || "").trim();
      const normalizedNameLastname = String(inviteNameLastname || "").trim();

      let payload:
        | { roomID: string; userID: string; email?: undefined; nameLastname?: undefined }
        | { roomID: string; email: string; nameLastname: string; userID?: undefined }
        | null = null;

      if (normalizedSelectedCollaboratorId) {
        if (normalizedSelectedCollaboratorId.startsWith("email:")) {
          setInviteActionError(t("memeetInvalidCollaboratorSelection", "Selected collaborator is invalid."));
          return;
        }
        payload = { roomID: roomId, userID: normalizedSelectedCollaboratorId };
      } else if (normalizedEmail) {
        if (!normalizedNameLastname) {
          setInviteActionError(
            t("memeetNameLastnameRequiredForEmailInvite", "Name and lastname are required for email invitation.")
          );
          return;
        }
        payload = { roomID: roomId, email: normalizedEmail, nameLastname: normalizedNameLastname };
      }

      if (!payload) {
        setInviteActionError(
          t("memeetInviteSelectionRequired", "Select a collaborator or fill the email invitation form.")
        );
        return;
      }

      setIsInviteSubmitting(true);
      setInviteActionError("");
      setInviteActionSuccess("");

      try {
        const selectedNotify = Object.keys(notifyChannels).filter(
          (k) => (notifyChannels as Record<string, boolean>)[k]
        );
        const response = await onInviteUserService({ ...payload, notify: selectedNotify });
        // Katilim baglantisi: backend doner (memeet.php JOIN_BASE). Kullanici
        // hicbir kanal secmediyse tek yolu budur.
        const link =
          (response as any)?.result?.joinLink ??
          (response as any)?.data?.result?.joinLink ??
          (response as any)?.joinLink ??
          "";
        if (typeof link === "string" && link) setInviteJoinLink(link);
        if (!readResponseStatus(response)) {
          setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
          return;
        }

        const latestInvitations = await fetchInvitations();
        await refreshInvitedChecks(collaborators, latestInvitations);

        setSelectedCollaboratorId("");
        setInviteEmail("");
        setInviteNameLastname("");
        // Backend hangi kanallarin gercekten gittigini doner (notified). Mail
        // secili ama gitmediyse kullanici bunu GORMELI, linki kendisi paylasir.
        const notified =
          (response as any)?.result?.notified ?? (response as any)?.data?.result?.notified ?? null;
        if (notified && typeof notified === "object") {
          const wantedMail = Boolean((notifyChannels as Record<string, boolean>).mail);
          if (wantedMail && notified.mail === false) {
            setInviteActionSuccess(
              t("memeetInvitationSentNoMail", "Davet oluşturuldu ama e-posta gönderilemedi — aşağıdaki bağlantıyı paylaşın.")
            );
          } else if (wantedMail && notified.mail === true) {
            setInviteActionSuccess(t("memeetInvitationSentWithMail", "Davet gönderildi, e-posta iletildi."));
          } else {
            setInviteActionSuccess(t("memeetInvitationSent", "Invitation sent."));
          }
        } else {
          setInviteActionSuccess(t("memeetInvitationSent", "Invitation sent."));
        }
        // Modal ACIK kalir: kullanici katilim baglantisini gorup kopyalayabilsin.
        // (Eskiden hemen kapaniyordu; link gosterilecegi icin artik kapanmiyor.)
      } catch {
        setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
      } finally {
        setIsInviteSubmitting(false);
      }
    },
    [
      onInviteUserService,
      notifyChannels,
      roomId,
      selectedCollaboratorId,
      inviteEmail,
      inviteNameLastname,
      t,
      fetchInvitations,
      refreshInvitedChecks,
      collaborators,
    ]
  );

  const handleConfirmRemoveInvitation = useCallback(async () => {
    const code = String(invitationToRemove?.invitationCode || "").trim();
    if (!onRemoveInvitationService || !code) return;

    setIsRemovingInvitation(true);
    setInviteActionError("");
    try {
      const response = await onRemoveInvitationService({ invitationCode: code });
      if (!readResponseStatus(response)) {
        setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
        return;
      }
      setInvitationToRemove(null);
      const latestInvitations = await fetchInvitations();
      await refreshInvitedChecks(collaborators, latestInvitations);
    } catch {
      setInviteActionError(t("somethingWentWrong", "Bir şeyler yanlış gitti."));
    } finally {
      setIsRemovingInvitation(false);
    }
  }, [
    invitationToRemove,
    onRemoveInvitationService,
    t,
    fetchInvitations,
    refreshInvitedChecks,
    collaborators,
  ]);

  const handleParticipantActionClick = useCallback(
    async (participant: Participant) => {
      if (!onSendDmOnSocialHive || participant.isLocal) return;
      const targetUserId = participant.userId;
      if (!targetUserId) return;
      await onSendDmOnSocialHive(targetUserId, participant.name);
    },
    [onSendDmOnSocialHive]
  );

  // -------------------------------------------------------------------------
  // localHandRaisedRef sync
  // -------------------------------------------------------------------------
  useEffect(() => {
    localHandRaisedRef.current = isHandRaised;
  }, [isHandRaised]);

  useEffect(() => {
    chatOpenRef.current = isChatOpen;
    if (isChatOpen) setUnreadChatCount(0);
  }, [isChatOpen]);

  useEffect(() => {
    if (!resolvedWorkspaces.length) return;
    if (selectedChecklistWorkspaceId && workspaceIdSet.has(String(selectedChecklistWorkspaceId))) return;
    setSelectedChecklistWorkspaceId(resolvedWorkspaces[0]?.id || "");
  }, [resolvedWorkspaces, selectedChecklistWorkspaceId, workspaceIdSet]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (!hasChecklistWorkspaceSelection) return;
      if (!selectedChecklistWorkspaceId) return;
      window.localStorage.setItem("memeet:lastChecklistWorkspaceId", String(selectedChecklistWorkspaceId));
    } catch {
      // ignore
    }
  }, [selectedChecklistWorkspaceId, hasChecklistWorkspaceSelection]);

  useEffect(() => {
    if (!roomId || !canLoadInvitations) return;
    void fetchInvitations();
  }, [roomId, canLoadInvitations, fetchInvitations]);

  useEffect(() => {
    if (!isInviteModalOpen) return;

    let cancelled = false;
    (async () => {
      const [nextCollaborators, nextInvitations] = await Promise.all([
        fetchCollaborators(),
        fetchInvitations(),
      ]);
      if (cancelled) return;
      await refreshInvitedChecks(nextCollaborators, nextInvitations);
    })();

    return () => {
      cancelled = true;
    };
  }, [isInviteModalOpen, fetchCollaborators, fetchInvitations, refreshInvitedChecks]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Escape") return;
        setChatContextMenu(null);
        if (!addingChecklistForMessageId) {
          setIsChecklistPickerOpen(false);
          setChecklistPickerMessageId(null);
          setChecklistActionError("");
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    } catch {
      return;
    }
  }, [addingChecklistForMessageId]);

  useEffect(() => {
    if (!chatContextMenu) return;

    const onClick = () => setChatContextMenu(null);
    const onScrollOrResize = () => setChatContextMenu(null);

    window.addEventListener("click", onClick);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [chatContextMenu]);

  // -------------------------------------------------------------------------
  // Prejoin (lobby) — kullanıcı LiveKit'e bağlanmadan önce kamera/mic ayarı yapar.
  // Otomatik connect YOK; sadece "Katıl" butonu lk.connect tetikler.
  // (State declaration'ları yukarıda — TS hoist için.)
  // -------------------------------------------------------------------------
  const hasConnectedRef = useRef(false);

  // Medya hatasını kullanıcı diline çevir (DOMException adı → kısa, yol gösteren metin).
  const mediaErrorText = useCallback(
    (kind: "audio" | "video", name: string) => {
      const dev = kind === "audio" ? t("memeetMicrophone", "Mikrofon") : t("memeetCamera", "Kamera");
      switch (name) {
        case "NotAllowedError":
        case "SecurityError":
          return t("memeetMediaDenied", "{device} izni verilmedi. Tarayıcının adres çubuğundaki kilit simgesinden izin verip sayfayı yenileyin.", { device: dev });
        case "NotFoundError":
          return t("memeetMediaNotFound", "{device} bulunamadı.", { device: dev });
        case "NotReadableError":
        case "AbortError":
          return t("memeetMediaBusy", "{device} başka bir uygulama tarafından kullanılıyor.", { device: dev });
        case "OverconstrainedError":
          return t("memeetMediaOverconstrained", "Seçili {device} artık bağlı değil; varsayılan cihaz kullanılacak.", { device: dev });
        default:
          return t("memeetMediaUnknown", "{device} açılamadı.", { device: dev });
      }
    },
    [t]
  );

  // Prejoin için getUserMedia — kullanıcı in-call'a girmeden önce preview göster.
  // 2026-10-01: Ses ve görüntü AYRI istenir (kamera yok/meşgul/izinsiz diye mikrofon
  // düşmesin), kayıtlı cihaz kimliği önce doğrulanır (farklı tarayıcı/profilde
  // `exact` kısıtı OverconstrainedError veriyordu) ve hata kullanıcıya gösterilir.
  useEffect(() => {
    if (callStatus !== "idle") return;
    if (prejoinStreamRef.current) return;
    let cancelled = false;
    (async () => {
      let audioId = selectedAudioInput;
      let videoId = selectedVideoInput;
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const ids = new Set(devs.map((d) => d.deviceId).filter(Boolean));
        if (audioId && !ids.has(audioId)) { audioId = null; setSelectedAudioInput(null); writeDevicePref(PREF_AUDIO_IN, ""); }
        if (videoId && !ids.has(videoId)) { videoId = null; setSelectedVideoInput(null); writeDevicePref(PREF_VIDEO_IN, ""); }
      } catch {
        // enumerateDevices yoksa kısıtsız dene
      }
      const tracks: MediaStreamTrack[] = [];
      let audioErr: string | null = null;
      let videoErr: string | null = null;
      try {
        const a = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(audioId ? { deviceId: { exact: audioId } } : {}),
            echoCancellation: true, noiseSuppression: true, autoGainControl: true,
          },
        });
        tracks.push(...a.getAudioTracks());
      } catch (e) {
        audioErr = (e as { name?: string })?.name || "UnknownError";
        console.error("[LiveKit] prejoin audio getUserMedia failed:", e);
      }
      try {
        const v = await navigator.mediaDevices.getUserMedia({
          video: videoId ? { deviceId: { exact: videoId } } : true,
        });
        tracks.push(...v.getVideoTracks());
      } catch (e) {
        videoErr = (e as { name?: string })?.name || "UnknownError";
        console.error("[LiveKit] prejoin video getUserMedia failed:", e);
      }
      if (cancelled) {
        tracks.forEach((t) => t.stop());
        return;
      }
      if (audioErr) {
        setPrejoinAudioEnabled(false);
        setMicPermissionDenied(audioErr === "NotAllowedError" || audioErr === "SecurityError");
      }
      if (videoErr) setPrejoinVideoEnabled(false);
      const notes: string[] = [];
      if (audioErr) notes.push(mediaErrorText("audio", audioErr));
      if (videoErr) notes.push(mediaErrorText("video", videoErr));
      if (videoErr && !audioErr) notes.push(t("memeetJoinAudioOnlyHint", "Sesle katılabilirsiniz."));
      setPrejoinNotice(notes.join(" "));
      if (!tracks.length) return;
      const s = new MediaStream(tracks);
      prejoinStreamRef.current = s;
      setPrejoinStream(s);
      // Track-level enabled flag uygulansın (kullanıcı önceden toggle yapmış olabilir)
      s.getAudioTracks().forEach((t) => (t.enabled = prejoinAudioEnabled));
      s.getVideoTracks().forEach((t) => (t.enabled = prejoinVideoEnabled));
    })();
    return () => {
      cancelled = true;
    };
  }, [callStatus]);

  // [MEMEET-VFX] On izleme: ham kamera izini LiveKit LocalVideoTrack'ine sarip efekti uygula; ayni iz icin
  // efekt degisince isleyici calisirken gecis yapar. Iz degisince / girisoncesi bitince isleyici durur
  // (ham iz prejoin akisina ait; userProvidedTrack=true oldugu icin burada durdurulmaz).
  const prejoinFxRef = useRef<{ raw: MediaStreamTrack; track: LocalVideoTrack } | null>(null);
  const dropPrejoinFx = useCallback(() => {
    const cur = prejoinFxRef.current;
    prejoinFxRef.current = null;
    if (cur) void cur.track.stopProcessor().catch(() => undefined);
    setPrejoinPreview(null);
  }, []);
  useEffect(() => {
    const raw = callStatus === "idle" ? prejoinStream?.getVideoTracks()[0] : undefined;
    if (!raw || raw.readyState === "ended" || videoEffect.mode === "none") {
      dropPrejoinFx();
      return;
    }
    let cancelled = false;
    setVfxBusy(true);
    (async () => {
      let cur = prejoinFxRef.current;
      if (cur && cur.raw !== raw) { dropPrejoinFx(); cur = null; }
      if (!cur) {
        cur = { raw, track: new LocalVideoTrack(raw, undefined, true) };
        prejoinFxRef.current = cur;
      }
      const ok = await applyVideoEffect(cur.track as any, videoEffect);
      if (cancelled) return;
      const pt = cur.track.getProcessor() ? cur.track.mediaStreamTrack : undefined; // isleyici varsa islenmis iz
      setPrejoinPreview(ok && pt ? new MediaStream([pt]) : null);
    })()
      .catch(() => { if (!cancelled) setPrejoinPreview(null); })
      .finally(() => { if (!cancelled) setVfxBusy(false); });
    return () => { cancelled = true; };
  }, [callStatus, prejoinStream, videoEffect, dropPrejoinFx]);
  useEffect(() => () => dropPrejoinFx(), [dropPrejoinFx]);

  // [MEMEET-VFX] Toplanti ici: secili efekti kancaya bildir (kamera yayinlaninca orada da uygulanir).
  useEffect(() => {
    let alive = true;
    const run = lk.setVideoEffect(videoEffect);
    if (callStatus === "in-call") {
      setVfxBusy(true);
      void Promise.resolve(run).finally(() => { if (alive) setVfxBusy(false); });
    }
    return () => { alive = false; };
  }, [videoEffect, callStatus]); // lk.setVideoEffect kararli (useCallback); lk nesnesi her render degisir

  const changeVideoEffect = useCallback((v: VideoEffect) => {
    setVideoEffectState(v);
    writeVideoEffect(v);
  }, []);

  // Prejoin stream'i durdur — joinCall sırasında ya da unmount'ta.
  const stopPrejoinStream = useCallback(() => {
    const s = prejoinStreamRef.current;
    if (!s) return;
    s.getTracks().forEach((t) => t.stop());
    prejoinStreamRef.current = null;
    setPrejoinStream(null);
  }, []);

  // Feature A — pre-join "İçeridekiler": henüz bağlanmadan (idle) backend'den
  // odadaki canlı katılımcıları çek + ~5sn poll. lk.participants bağlanmadan boş
  // olduğu için strip ancak bu veriyle görünür. Bağlanınca poll durur.
  useEffect(() => {
    if (callStatus !== "idle") return;
    if (!onGetRoomParticipants || !roomId) return;

    let cancelled = false;
    const load = async () => {
      try {
        const items = await onGetRoomParticipants(roomId);
        if (cancelled || !Array.isArray(items)) return;
        const mapped: PreConnectParticipant[] = items
          .map((item) => {
            const parsed = parseIdentityNickname(item?.name || "");
            return {
              id: String(item?.identity || ""),
              displayName: parsed.displayName || item?.name || item?.identity || "",
              avatarUrl: parsed.avatarUrl,
              lodosUserId: parsed.lodosUserId,
            };
          })
          .filter((p) => p.id && p.displayName);
        setPreConnectParticipants(mapped);
      } catch {
        // presence best-effort — sessiz geç
      }
    };

    void load();
    const interval = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [callStatus, onGetRoomParticipants, roomId]);

  // Feature B — prejoin'da seçilen cihazlarla getUserMedia stream'ini yeniden kur.
  const rebuildPrejoinStream = useCallback(
    async (override?: { audioDeviceId?: string; videoDeviceId?: string }) => {
      const audioId = override?.audioDeviceId ?? selectedAudioInput ?? undefined;
      const videoId = override?.videoDeviceId ?? selectedVideoInput ?? undefined;
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(audioId ? { deviceId: { exact: audioId } } : {}),
            echoCancellation: true, noiseSuppression: true, autoGainControl: true,
          },
          video: videoId ? { deviceId: { exact: videoId } } : true,
        });
        const old = prejoinStreamRef.current;
        if (old) old.getTracks().forEach((t) => t.stop());
        s.getAudioTracks().forEach((t) => (t.enabled = prejoinAudioEnabled));
        s.getVideoTracks().forEach((t) => (t.enabled = prejoinVideoEnabled));
        prejoinStreamRef.current = s;
        setPrejoinStream(s);
      } catch (e) {
        console.error("[LiveKit] rebuild prejoin stream failed:", e);
      }
    },
    [selectedAudioInput, selectedVideoInput, prejoinAudioEnabled, prejoinVideoEnabled]
  );

  const handleSelectAudioInput = useCallback(
    (deviceId: string) => {
      setSelectedAudioInput(deviceId);
      writeDevicePref(PREF_AUDIO_IN, deviceId);
      if (callStatus === "idle") {
        void rebuildPrejoinStream({ audioDeviceId: deviceId });
      } else {
        void lk.switchDevice("audioinput", deviceId).catch(() => undefined);
      }
    },
    [callStatus, lk, rebuildPrejoinStream]
  );

  const handleSelectVideoInput = useCallback(
    (deviceId: string) => {
      setSelectedVideoInput(deviceId);
      writeDevicePref(PREF_VIDEO_IN, deviceId);
      if (callStatus === "idle") {
        void rebuildPrejoinStream({ videoDeviceId: deviceId });
      } else {
        void lk.switchDevice("videoinput", deviceId).catch(() => undefined);
      }
    },
    [callStatus, lk, rebuildPrejoinStream]
  );

  const handleSelectAudioOutput = useCallback(
    (deviceId: string) => {
      setSelectedAudioOutput(deviceId);
      writeDevicePref(PREF_AUDIO_OUT, deviceId);
      // Remote element'lere setSinkId ParticipantsGrid içinde audioOutputDeviceId
      // prop'u ile uygulanır; ayrıca LiveKit'in kendi output sink'ini de yönlendir.
      if (callStatus !== "idle") {
        void lk.switchDevice("audiooutput", deviceId).catch(() => undefined);
      }
    },
    [callStatus, lk]
  );

  useEffect(() => {
    return () => {
      const s = prejoinStreamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      prejoinStreamRef.current = null;
    };
  }, []);

  // Drive callStatus from LiveKit connection state. Kullanıcı butona basıp connect
  // başlattıktan SONRA bu effect connecting → in-call → ended geçişlerini idare eder.
  useEffect(() => {
    if (lk.connectionState === ConnectionState.Connected) {
      setCallStatus("in-call");
    } else if (lk.connectionState === ConnectionState.Connecting) {
      setCallStatus((prev) => (prev === "in-call" ? prev : "connecting"));
    } else if (lk.connectionState === ConnectionState.Disconnected) {
      setCallStatus((prev) => (prev === "in-call" ? "ended" : prev));
    }
  }, [lk.connectionState]);

  // Audio context cleanup on unmount
  useEffect(() => {
    return () => {
      const audioContext = notificationAudioContextRef.current;
      notificationAudioContextRef.current = null;
      if (!audioContext || audioContext.state === "closed") return;
      void audioContext.close().catch(() => undefined);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Derive remoteStreams + remoteParticipants from LiveKit participants
  // -------------------------------------------------------------------------
  const remoteStreams = useMemo(() => {
    const m = new Map<string, MediaStream>();
    for (const p of lk.participants) {
      const tracks: MediaStreamTrack[] = [];
      if (p.audioTrack) tracks.push(p.audioTrack);
      if (p.videoTrack) tracks.push(p.videoTrack);
      if (p.screenShareTrack) tracks.push(p.screenShareTrack);
      if (tracks.length) m.set(p.identity, getCachedStream(`remote:${p.identity}`, tracks));
    }
    return m;
  }, [lk.participants, getCachedStream]);

  const remoteParticipants = useMemo(() => {
    const m = new Map<string, RemoteParticipant>();
    for (const p of lk.participants) {
      const identity = parseIdentityNickname(p.name || "");
      m.set(p.identity, {
        id: p.identity,
        displayName: identity.displayName || p.name || p.identity,
        avatarUrl: identity.avatarUrl,
        lodosUserId: identity.lodosUserId,
        isAudioEnabled: !p.audioMuted,
        isVideoEnabled: !p.videoMuted,
        isScreenSharing: !!p.screenShareTrack,
        screenShareMid: null,
        isHandRaised: handRaisedByIdentity.get(p.identity) ?? false,
      });
    }
    return m;
  }, [lk.participants, handRaisedByIdentity]);

  // [MEMEET-HANDORDER] 2026-11-05: el kaldiranlar, kalkma zamanina gore (ilk kaldiran 1.).
  const handQueue = useMemo(() => {
    const items: { identity: string; name: string; at: number; isLocal: boolean }[] = [];
    for (const [id, raised] of handRaisedByIdentity.entries()) {
      if (!raised) continue;
      const rp = remoteParticipants.get(id);
      if (!rp) continue;
      items.push({ identity: id, name: rp.displayName, at: handRaisedAtByIdentity.get(id) ?? Number.MAX_SAFE_INTEGER, isLocal: false });
    }
    if (isHandRaised) {
      items.push({ identity: localIdentity || "local", name: effectiveDisplayName, at: localHandRaisedAt ?? Number.MAX_SAFE_INTEGER, isLocal: true });
    }
    items.sort((a, b) => a.at - b.at || a.name.localeCompare(b.name));
    return items;
  }, [handRaisedByIdentity, handRaisedAtByIdentity, remoteParticipants, isHandRaised, localHandRaisedAt, localIdentity, effectiveDisplayName]);
  const handOrderByIdentity = useMemo(() => {
    const m = new Map<string, number>();
    handQueue.forEach((h, i) => m.set(h.identity, i + 1));
    return m;
  }, [handQueue]);

  // Notify-on-join: detect newly joined participants and play tone
  const knownParticipantIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (callStatus !== "in-call") {
      knownParticipantIdsRef.current = new Set(lk.participants.map((p) => p.identity));
      return;
    }
    const currentIds = new Set(lk.participants.map((p) => p.identity));
    const known = knownParticipantIdsRef.current;
    let hasNewJoiner = false;
    for (const id of currentIds) {
      if (!known.has(id)) {
        hasNewJoiner = true;
        break;
      }
    }
    knownParticipantIdsRef.current = currentIds;
    if (hasNewJoiner) playNotificationTone("join");
  }, [lk.participants, callStatus, playNotificationTone]);

  // -------------------------------------------------------------------------
  // Chat state helpers (transport-agnostic)
  // -------------------------------------------------------------------------
  const appendChatMessage = useCallback((message: ChatMessage, opts?: { bumpUnread?: boolean }) => {
    setChatMessages((prev) => {
      if (chatMessageIdsRef.current.has(message.id)) return prev;
      chatMessageIdsRef.current.add(message.id);
      const next = [...prev, message];

      if (next.length > MAX_CHAT_MESSAGES) {
        const overflow = next.length - MAX_CHAT_MESSAGES;
        const removed = next.splice(0, overflow);
        removed.forEach((m) => chatMessageIdsRef.current.delete(m.id));
      }
      return next;
    });

    if (opts?.bumpUnread && !chatOpenRef.current) {
      setUnreadChatCount((c) => Math.min(99, c + 1));
      playNotificationTone("chat");
    }
  }, [playNotificationTone]);

  const applyChecklistAddedEvent = useCallback((ev: ChatWireChecklistAddedV1) => {
    setChecklistAddsByMessageId((prev) => {
      const current = prev[ev.messageId] || [];
      const workspaceId = String(ev.workspaceId || "").trim();
      const checklistName = String(ev.checklistName || "").trim();
      if (!workspaceId || !checklistName) return prev;

      const already = current.some(
        (x) =>
          String(x.workspaceId) === workspaceId &&
          String(x.checklistId || "") === String(ev.checklistId || "") &&
          String(x.checklistName) === checklistName
      );
      if (already) return prev;

      const nextItem: ChatChecklistAddMeta = {
        workspaceId,
        checklistId: ev.checklistId ? String(ev.checklistId) : undefined,
        checklistName,
        byUserId: ev.byUserId ? String(ev.byUserId) : undefined,
        byName: ev.byName ? String(ev.byName) : undefined,
        addedAt: typeof ev.ts === "number" ? ev.ts : Date.now(),
        preview: ev.preview ? String(ev.preview) : undefined,
      };

      return { ...prev, [ev.messageId]: [...current, nextItem] };
    });
  }, []);

  const broadcastChecklistAddedEvent = useCallback((ev: ChatWireChecklistAddedV1) => {
    const payloadObj: ChatWireMessageV1 = {
      v: 1,
      type: "chat-message",
      id: makeChatId(),
      ts: typeof ev.ts === "number" ? ev.ts : Date.now(),
      text: " ",
      from: {
        name: String(effectiveDisplayName || "User"),
        avatarUrl: avatarUrl || undefined,
      },
      meta: {
        kind: "checklist-added",
        messageId: String(ev.messageId || ""),
        workspaceId: String(ev.workspaceId || ""),
        checklistName: String(ev.checklistName || ""),
        checklistId: ev.checklistId ? String(ev.checklistId) : undefined,
        byUserId: ev.byUserId ? String(ev.byUserId) : undefined,
        byName: ev.byName ? String(ev.byName) : undefined,
        preview: ev.preview ? String(ev.preview) : undefined,
      },
    };

    void lk.sendData(JSON.stringify(payloadObj), true).catch((error) => {
      console.error("Failed to send checklist added chat event:", error);
    });
  }, [effectiveDisplayName, avatarUrl, lk]);

  const openChatContextMenu = useCallback((e: MouseEvent, messageId: string) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window === "undefined") return;
      const menuWidth = 220;
      const menuHeight = 96;
      const padding = 8;

      const x = Math.min(e.clientX, window.innerWidth - menuWidth - padding);
      const y = Math.min(e.clientY, window.innerHeight - menuHeight - padding);

      setChatContextMenu({
        messageId,
        x: Math.max(padding, x),
        y: Math.max(padding, y),
      });
    } catch {
      // ignore
    }
  }, []);

  const openChecklistPickerForMessage = useCallback((messageId: string) => {
    setChecklistActionError("");
    setChecklistPickerMessageId(messageId);
    setIsChecklistPickerOpen(true);
  }, []);

  const openAddToChecklistPicker = useCallback(() => {
    const messageId = chatContextMenu?.messageId;
    setChatContextMenu(null);
    if (!messageId) return;
    openChecklistPickerForMessage(messageId);
  }, [chatContextMenu, openChecklistPickerForMessage]);

  const closeAddToChecklistPicker = useCallback((opts?: { force?: boolean }) => {
    if (!opts?.force && addingChecklistForMessageId) return;
    setIsChecklistPickerOpen(false);
    setChecklistPickerMessageId(null);
    setChecklistActionError("");
  }, [addingChecklistForMessageId]);

  const handleAddToNotes = useCallback(
    async (messageId: string) => {
      if (!canAddToNotes || isAddingToNotesForMessageId) return;
      const msg = chatMessages.find((m) => m.id === messageId);
      if (!msg) return;

      setIsAddingToNotesForMessageId(messageId);
      try {
        const result = await onAddChatMessageToNotes?.({
          meetingId: String(roomId || ""),
          meetingStartedAt: meetingStartedAtRef.current || Date.now(),
          message: {
            id: msg.id,
            text: msg.text,
            ts: msg.ts,
            fromId: msg.fromId,
            fromName: msg.fromName,
          },
        });

        setNotesAddedByMessageId((prev) => ({
          ...prev,
          [messageId]: {
            addedAt: Date.now(),
            noteId: result?.noteId,
            noteTitle: result?.noteTitle,
          },
        }));
      } catch (error) {
        console.error("Failed to add chat message to notes:", error);
      } finally {
        setIsAddingToNotesForMessageId(null);
      }
    },
    [canAddToNotes, chatMessages, isAddingToNotesForMessageId, onAddChatMessageToNotes, roomId]
  );

  const addChatMessageToChecklist = useCallback(
    async (messageId: string, workspaceId: string, opts?: { closePicker?: boolean }) => {
      if (!canAddToChecklist) {
        setChecklistActionError(t("memeetChecklistIntegrationMissing", "Checklist integration is unavailable."));
        return;
      }

      const wsId = String(workspaceId || "").trim();
      if (!wsId) {
        setChecklistActionError(t("memeetSelectWorkspace", "Please select a workspace."));
        return;
      }

      const msg = chatMessages.find((m) => m.id === messageId);
      if (!msg) {
        setChecklistActionError(t("memeetMessageNotFound", "Message not found."));
        return;
      }

      setChecklistActionError("");
      setAddingChecklistForMessageId(messageId);
      const now = Date.now();

      try {
        const result = await onAddChatMessageToChecklist?.({
          workspaceId: wsId,
          meetingId: String(roomId || ""),
          meetingStartedAt: meetingStartedAtRef.current || now,
          message: {
            id: msg.id,
            text: msg.text,
            ts: msg.ts,
            fromId: msg.fromId,
            fromName: msg.fromName,
          },
        });

        const checklistName = String(result?.checklistName || roomId || "").trim() || t("memeetChecklist", "Checklist");
        const checklistId = result?.checklistId ? String(result.checklistId) : undefined;
        const preview = truncateChatPreview(msg.text);

        applyChecklistAddedEvent({
          v: 1,
          type: "chat-checklist-added",
          ts: now,
          messageId: msg.id,
          workspaceId: wsId,
          checklistName,
          checklistId,
          byUserId: lodosUserId ? String(lodosUserId) : undefined,
          byName: effectiveDisplayName ? String(effectiveDisplayName) : undefined,
          preview,
        });

        broadcastChecklistAddedEvent({
          v: 1,
          type: "chat-checklist-added",
          ts: now,
          messageId: msg.id,
          workspaceId: wsId,
          checklistName,
          checklistId,
          byUserId: lodosUserId ? String(lodosUserId) : undefined,
          byName: effectiveDisplayName ? String(effectiveDisplayName) : undefined,
          preview,
        });

        if (opts?.closePicker) {
          setHasChecklistWorkspaceSelection(true);
          try {
            if (typeof window !== "undefined") {
              window.localStorage.setItem("memeet:hasChecklistWorkspaceSelection", "1");
              window.localStorage.setItem("memeet:lastChecklistWorkspaceId", String(wsId));
            }
          } catch {
            // ignore
          }
          closeAddToChecklistPicker({ force: true });
        }
      } catch (error) {
        console.error("Failed to add chat message to checklist:", error);
        setChecklistActionError(t("memeetChecklistAddFailed", "Couldn't add to checklist."));
      } finally {
        setAddingChecklistForMessageId(null);
      }
    },
    [
      canAddToChecklist,
      chatMessages,
      onAddChatMessageToChecklist,
      roomId,
      applyChecklistAddedEvent,
      broadcastChecklistAddedEvent,
      t,
      lodosUserId,
      effectiveDisplayName,
      closeAddToChecklistPicker,
    ]
  );

  const handleConfirmAddToChecklist = useCallback(async () => {
    if (!checklistPickerMessageId) return;
    const workspaceId = String(selectedChecklistWorkspaceId || "").trim();
    await addChatMessageToChecklist(checklistPickerMessageId, workspaceId, { closePicker: true });
  }, [addChatMessageToChecklist, checklistPickerMessageId, selectedChecklistWorkspaceId]);

  // -------------------------------------------------------------------------
  // Kayıt durumu bildirimi — odadaki herkese (sonradan katılana da)
  // -------------------------------------------------------------------------
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingNotice, setRecordingNotice] = useState<{ on: boolean; name?: string } | null>(null);
  const recNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!summaryApi?.getRecordingState || !roomId) return;
    let alive = true;
    Promise.resolve(summaryApi.getRecordingState(roomId))
      .then((r: any) => {
        const d = r && r.data && r.data.result !== undefined ? r.data.result : (r && r.data) || r;
        if (alive && d && typeof d.enabled !== "undefined") setRecordingActive(!!d.enabled);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [summaryApi, roomId]);

  const handleRecordingChange = useCallback(
    (on: boolean) => {
      setRecordingActive(on);
      void lk
        .sendData(
          JSON.stringify({ v: 1, type: "recording-state", on, fromName: effectiveDisplayName, ts: Date.now() }),
          true
        )
        .catch(() => undefined);
    },
    [lk, effectiveDisplayName]
  );

  // -------------------------------------------------------------------------
  // Moderatör (host) mute kontrolü + bildirim toast'ı
  // -------------------------------------------------------------------------
  // Oda sahibi = createdBy.id (MeMeetApp getRoom'dan çözüp creatorUserId prop'uyla
  // geçer). Bir katılımcı host'tur: decoded lodosUserId === hostUserId. Yerel
  // kullanıcı host'tur: lodosUserId === hostUserId (== isRoomCreator).
  const hostUserId = String(creatorUserId || "").trim();
  const isLocalUserHost = isRoomCreator;
  const selfMicToggleAtRef = useRef<number>(0);

  const [muteNotice, setMuteNotice] = useState<{ tone: "info" | "error"; text: string } | null>(null);
  const muteNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showMuteNotice = useCallback((tone: "info" | "error", text: string) => {
    setMuteNotice({ tone, text });
    if (muteNoticeTimerRef.current) clearTimeout(muteNoticeTimerRef.current);
    muteNoticeTimerRef.current = setTimeout(() => setMuteNotice(null), 4500);
  }, []);
  useEffect(() => {
    return () => {
      if (muteNoticeTimerRef.current) clearTimeout(muteNoticeTimerRef.current);
    };
  }, []);

  // Host bir katilimciyi susturabilir (muted=true) ve GERI ACABILIR (muted=false).
  // Backend zaten iki yonu de destekliyor (memeet.php muteParticipant -> LiveKit
  // MutePublishedTrack); eski arayuz yalnizca true gonderiyordu, bu yuzden sesi
  // yalnizca kisinin kendisi acabiliyordu. Karsi taraf her iki durumda da bilgilendirilir
  // (TrackMuted/TrackUnmuted dinleyicileri).
  const handleMuteParticipant = useCallback(
    async (participant: Participant, muted: boolean) => {
      const identity = String(participant.identity || participant.id || "").trim();
      if (!onMuteParticipant || !roomId || !identity) return;
      const failMsg = muted
        ? t("memeetMuteFailed", "Katılımcı sessize alınamadı")
        : t("memeetUnmuteFailed", "Katılımcının mikrofonu açılamadı");
      try {
        const ok = await onMuteParticipant({ roomID: roomId, identity, muted });
        if (!ok) showMuteNotice("error", failMsg);
      } catch {
        showMuteNotice("error", failMsg);
      }
    },
    [onMuteParticipant, roomId, showMuteNotice, t]
  );

  // -------------------------------------------------------------------------
  // Video kaydı (tüm toplantı — grid + ekran paylaşımı) — host kontrolü +
  // TÜM katılımcılara görünen "Kayıt ediliyor" rıza göstergesi (state poll).
  // -------------------------------------------------------------------------
  const [videoRecording, setVideoRecording] = useState(false); // gerçek/poll edilmiş durum (herkes)
  const [videoRecBusy, setVideoRecBusy] = useState(false); // host butonu in-flight
  const [videoRecConsentOpen, setVideoRecConsentOpen] = useState(false);
  const [videoRecToast, setVideoRecToast] = useState<{ tone: "info" | "error"; text: string } | null>(null);
  const videoRecToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canControlVideoRecording =
    isLocalUserHost &&
    typeof onStartVideoRecording === "function" &&
    typeof onStopVideoRecording === "function";

  const showVideoRecToast = useCallback((tone: "info" | "error", text: string) => {
    setVideoRecToast({ tone, text });
    if (videoRecToastTimerRef.current) clearTimeout(videoRecToastTimerRef.current);
    videoRecToastTimerRef.current = setTimeout(() => setVideoRecToast(null), 4500);
  }, []);
  useEffect(() => {
    return () => {
      if (videoRecToastTimerRef.current) clearTimeout(videoRecToastTimerRef.current);
    };
  }, []);

  // Rıza göstergesi için durum poll'u — in-call'da her ~5sn, herkes çağırır.
  useEffect(() => {
    if (callStatus !== "in-call") return;
    if (typeof onGetVideoRecordingState !== "function" || !roomId) return;
    let alive = true;
    const poll = async () => {
      try {
        const on = await onGetVideoRecordingState({ roomID: roomId });
        if (alive) setVideoRecording(Boolean(on));
      } catch {
        // sessiz — feature yoksa/başarısızsa gösterge kapalı kalır
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), 5000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [callStatus, onGetVideoRecordingState, roomId]);

  const requestStartVideoRecording = useCallback(() => {
    if (!canControlVideoRecording || videoRecBusy) return;
    setVideoRecConsentOpen(true);
  }, [canControlVideoRecording, videoRecBusy]);

  const confirmStartVideoRecording = useCallback(async () => {
    setVideoRecConsentOpen(false);
    if (!onStartVideoRecording || !roomId) return;
    setVideoRecBusy(true);
    try {
      const ok = await onStartVideoRecording({ roomID: roomId });
      if (ok) {
        setVideoRecording(true);
        showVideoRecToast("info", t("memeetRecordingStarted", "Kayıt başladı"));
      } else {
        showVideoRecToast("error", t("memeetRecordFailed", "Kayıt başlatılamadı"));
      }
    } catch {
      showVideoRecToast("error", t("memeetRecordFailed", "Kayıt başlatılamadı"));
    } finally {
      setVideoRecBusy(false);
    }
  }, [onStartVideoRecording, roomId, showVideoRecToast, t]);

  const stopVideoRecording = useCallback(async () => {
    if (!onStopVideoRecording || !roomId || videoRecBusy) return;
    setVideoRecBusy(true);
    try {
      const ok = await onStopVideoRecording({ roomID: roomId });
      if (ok) {
        setVideoRecording(false);
        showVideoRecToast("info", t("memeetRecordingStopped", "Kayıt durduruldu"));
      } else {
        showVideoRecToast("error", t("memeetRecordFailed", "Kayıt başlatılamadı"));
      }
    } catch {
      showVideoRecToast("error", t("memeetRecordFailed", "Kayıt başlatılamadı"));
    } finally {
      setVideoRecBusy(false);
    }
  }, [onStopVideoRecording, roomId, videoRecBusy, showVideoRecToast, t]);

  // Moderatör yerel mikrofonumuzu zorla kapattığında LiveKit TrackMuted olayı
  // yerel mic publication'ında tetiklenir. Kendi toggle'ımızdan ayırmak için son
  // self-toggle zaman damgasına bakarız (yakınsa kullanıcının kendi işlemi).
  useEffect(() => {
    const room = lk.room;
    if (!room) return;

    const onTrackMuted = (publication: any, participant: any) => {
      try {
        if (!participant?.isLocal) return;
        const source = String(publication?.source || publication?.track?.source || "");
        if (source && source !== "microphone") return;
        const sinceSelfToggle = Date.now() - selfMicToggleAtRef.current;
        if (sinceSelfToggle < 1500) return; // kendi kapattık — toast gösterme
        showMuteNotice("info", t("memeetMutedByHost", "Moderatör mikrofonunuzu kapattı"));
      } catch {
        // ignore
      }
    };

    // Moderator mikrofonumuzu ZORLA ACTIGINDA da haber ver: sessiz sedasiz acilmis
    // bir mikrofon gizlilik sorunudur, kullanici her zaman gormeli.
    const onTrackUnmuted = (publication: any, participant: any) => {
      try {
        if (!participant?.isLocal) return;
        const source = String(publication?.source || publication?.track?.source || "");
        if (source && source !== "microphone") return;
        const sinceSelfToggle = Date.now() - selfMicToggleAtRef.current;
        if (sinceSelfToggle < 1500) return; // kendi actik
        showMuteNotice("info", t("memeetUnmutedByHost", "Moderatör mikrofonunuzu açtı"));
      } catch {
        // ignore
      }
    };

    room.on(RoomEvent.TrackMuted, onTrackMuted);
    room.on(RoomEvent.TrackUnmuted, onTrackUnmuted);
    // Girişte mikrofon kapalıysa bir kez hatırlat (2026-10-01): kullanıcılar kapalı
    // girdiğini fark etmiyor, "nasıl açacağımı bilemedim" diyordu.
    try {
      if (room.localParticipant && room.localParticipant.isMicrophoneEnabled !== true && !micPermissionDenied) {
        showMuteNotice("info", t("memeetJoinedMuted", "Mikrofonunuz kapalı — açmak için alttaki mikrofon düğmesine basın."));
      }
    } catch {
      // ignore
    }
    return () => {
      try {
        room.off(RoomEvent.TrackMuted, onTrackMuted);
        room.off(RoomEvent.TrackUnmuted, onTrackUnmuted);
      } catch {
        // ignore
      }
    };
  }, [lk.room, showMuteNotice, t]);

  // -------------------------------------------------------------------------
  // LiveKit DataReceived listener (chat / soundboard / hand-state / checklist)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const room = lk.room;
    if (!room) return;

    const onData = (payload: Uint8Array, participant?: any) => {
      const raw = decodeRtcData(payload);
      if (!raw) return;

      // soundboard
      const soundboardMsg = parseSoundboardWireMessageV1(raw);
      if (soundboardMsg) {
        playSoundboardSound(soundboardMsg.soundId);
        return;
      }

      // checklist-added (sent as chat-message envelope with meta)
      const checklistAdded = parseChatWireChecklistAddedV1(raw);
      if (checklistAdded) {
        applyChecklistAddedEvent(checklistAdded);
        return;
      }

      // recording-state (kayıt başladı/durdu)
      const recState = parseRecordingStateWireV1(raw);
      if (recState) {
        setRecordingActive(recState.on);
        setRecordingNotice({ on: recState.on, name: recState.fromName });
        if (recNoticeTimerRef.current) clearTimeout(recNoticeTimerRef.current);
        recNoticeTimerRef.current = setTimeout(() => setRecordingNotice(null), 5500);
        return;
      }

      // tepki (reaction)
      const reaction = parseReactionWireV1(raw);
      if (reaction) {
        const fromId = String(participant?.identity || "");
        const lkp = lk.participants.find((p) => p.identity === fromId);
        const fallback = parseIdentityNickname(lkp?.name || "").displayName || lkp?.name || "";
        pushFloatingReaction(reaction.kind, reaction.fromName || fallback || t("memeetSomeone", "Bir katılımcı"));
        return;
      }

      // hand-state
      const handState = parseHandStateWireV1(raw);
      if (handState) {
        const fromId = String(participant?.identity || "");
        if (!fromId) return;
        // Yalnız false→true geçişinde çın çal (moderatör + tüm katılımcılar duyar).
        const wasRaised = handRaisedByIdentityRef.current.get(fromId) === true;
        if (handState.raised && !wasRaised) playHandRaiseChime();
        setHandRaisedByIdentity((prev) => {
          const next = new Map(prev);
          next.set(fromId, handState.raised);
          return next;
        });
        // [MEMEET-HANDORDER] kalkma zamani: zaten kalkiksa (yeniden yayin) ilk zaman korunur.
        setHandRaisedAtByIdentity((prev) => {
          const next = new Map(prev);
          if (handState.raised) {
            const at = typeof handState.ts === "number" && handState.ts > 0 ? handState.ts : Date.now();
            const cur = next.get(fromId);
            next.set(fromId, wasRaised && cur !== undefined ? Math.min(cur, at) : at);
          } else {
            next.delete(fromId);
          }
          return next;
        });
        return;
      }

      // regular chat message
      const parsed = parseChatWireMessageV1(raw);
      const text = String((parsed?.text ?? raw) || "").trim();
      const attachment = parsed?.attachment;
      if (!text && !attachment) return;

      const fromId = String(participant?.identity || "");
      const lkParticipant = lk.participants.find((p) => p.identity === fromId);
      const fallbackName = parseIdentityNickname(lkParticipant?.name || "").displayName || lkParticipant?.name || fromId;
      const fromName = String((parsed?.fromName ?? fallbackName ?? "User") || "User").trim() || "User";
      const fromAvatarUrl = parsed?.fromAvatarUrl;

      appendChatMessage(
        {
          id: parsed?.id ?? makeChatId(),
          text,
          ts: parsed?.ts ?? Date.now(),
          fromId,
          fromName,
          fromAvatarUrl,
          isLocal: false,
          attachment,
        },
        { bumpUnread: true }
      );
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      try {
        room.off(RoomEvent.DataReceived, onData);
        if (recNoticeTimerRef.current) clearTimeout(recNoticeTimerRef.current);
      } catch {
        // ignore
      }
    };
  }, [lk.room]);

  // -------------------------------------------------------------------------
  // Call status side effects
  // -------------------------------------------------------------------------
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const inCall = callStatus === "in-call";
      window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall } }));
    } catch {
      // ignore
    }
  }, [callStatus]);

  useEffect(() => {
    return () => {
      try {
        if (typeof window === "undefined") return;
        window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall: false } }));
      } catch {
        // ignore
      }
    };
  }, []);

  // Set preview video element. Prejoin'da getUserMedia preview, in-call'da LiveKit local stream'i.
  useEffect(() => {
    if (!previewVideoRef.current) return;
    const previewSource = callStatus === "idle" ? (prejoinPreview || prejoinStream) : localStream; // [MEMEET-VFX]
    if (previewSource && previewVideoRef.current.srcObject !== previewSource) {
      previewVideoRef.current.srcObject = previewSource;
    }
  }, [localStream, prejoinStream, isVideoEnabled, callStatus, prejoinPreview]);

  // -------------------------------------------------------------------------
  // Chat scroll lock and link previews
  // -------------------------------------------------------------------------
  const updateChatScrollLock = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const threshold = 48;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollChatRef.current = distanceToBottom < threshold;
  }, []);

  useEffect(() => {
    if (!isChatOpen) return;
    const el = chatListRef.current;
    if (!el) return;
    if (!shouldAutoScrollChatRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages.length, isChatOpen]);

  useEffect(() => {
    if (!getLinkPreview) return;
    const authToken =
      token ||
      (typeof window !== "undefined" ? window.localStorage?.getItem("token") : "") ||
      "";
    if (!authToken) return;
    const allUrls = new Set<string>();
    chatMessages.forEach((m) => {
      extractUrls(m.text).forEach((u) => allUrls.add(u));
    });
    const toFetch = Array.from(allUrls).filter((u) => !linkPreviews[u] && !fetchingUrlsRef.current.has(u));
    if (toFetch.length === 0) return;

    let canceled = false;
    (async () => {
      for (const url of toFetch) {
        if (canceled) return;
        try {
          fetchingUrlsRef.current.add(url);
          const res = await getLinkPreview(authToken, url);
          const ok = Boolean(res?.data?.status);
          const result = res?.data?.result;
          if (!ok || !result) {
            if (!canceled) setLinkPreviews((prev) => ({ ...prev, [url]: { error: true } }));
            continue;
          }

          const nextUrl = normalizeUrl(result.url || url) || url;
          if (!canceled) {
            setLinkPreviews((prev) => ({
              ...prev,
              [url]: {
                title: String(result.title || ""),
                description: String(result.description || ""),
                image: result.image ? String(result.image) : null,
                url: nextUrl,
              },
            }));
          }
        } catch {
          if (!canceled) setLinkPreviews((prev) => ({ ...prev, [url]: { error: true } }));
        } finally {
          fetchingUrlsRef.current.delete(url);
        }
      }
    })();

    return () => {
      canceled = true;
    };
  }, [chatMessages, getLinkPreview, token, linkPreviews]);

  useEffect(() => {
    if (!isChatOpen) return;
    window.setTimeout(() => {
      chatDraftInputRef.current?.focus();
    }, 0);
  }, [isChatOpen]);

  // -------------------------------------------------------------------------
  // End call / back / disconnect
  // -------------------------------------------------------------------------
  const handleEndCall = useCallback(async () => {
    // Çıkmadan önce el kalkıksa "indir" mesajı yolla — diğer participantlar
    // ghost raise hand görmesin.
    if (localHandRaisedRef.current) {
      try {
        await lk.sendData(
          JSON.stringify({ v: 1, type: "hand-state", raised: false, fromName: effectiveDisplayName, ts: Date.now() }),
          true,
        );
      } catch {
        // ignore
      }
      localHandRaisedRef.current = false;
      setIsHandRaised(false);
    }

    setCallStatus("ended");
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("memeet:call-status", { detail: { inCall: false } }));
      }
    } catch {
      // ignore
    }

    try {
      await lk.disconnect();
    } catch (err) {
      console.error("[LiveKit] disconnect error:", err);
    }

    chatMessageIdsRef.current.clear();
    setChatMessages([]);
    setChatDraft("");
    setIsChatOpen(false);
    setUnreadChatCount(0);
    setChatContextMenu(null);
    setIsChecklistPickerOpen(false);
    setChecklistPickerMessageId(null);
    setChecklistActionError("");
    setAddingChecklistForMessageId(null);
    setIsAddingToNotesForMessageId(null);
    setChecklistAddsByMessageId({});
    setNotesAddedByMessageId({});
    meetingStartedAtRef.current = Date.now();
    setHandRaisedByIdentity(new Map());

    setCallDuration(0);
    if (autoLowerHandTimeoutRef.current) {
      window.clearTimeout(autoLowerHandTimeoutRef.current);
      autoLowerHandTimeoutRef.current = null;
    }
    if (autoLowerHandSilenceTimeoutRef.current) {
      window.clearTimeout(autoLowerHandSilenceTimeoutRef.current);
      autoLowerHandSilenceTimeoutRef.current = null;
    }
    localHandRaisedRef.current = false;
    setIsHandRaised(false);
    onCallEnd?.();

    setTimeout(() => setCallStatus("idle"), 1500);
  }, [lk, onCallEnd]);

  const endCall = useCallback(() => {
    void handleEndCall();
  }, [handleEndCall]);

  const requestEndCall = useCallback(() => {
    // Eski WebRTC akışında bir confirmation modal "memeet:request-end-call"u
    // yakalayıp "memeet:confirm-end-call" emit ediyordu. LiveKit branch'inde
    // bu modal yok, o yüzden direkt endCall.
    void handleEndCall();
  }, [handleEndCall]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const handler = () => {
        endCall();
      };
      window.addEventListener("memeet:confirm-end-call", handler);
      return () => window.removeEventListener("memeet:confirm-end-call", handler);
    } catch {
      return;
    }
  }, [endCall]);

  // -------------------------------------------------------------------------
  // Screen share (LiveKit)
  // -------------------------------------------------------------------------
  // Ekran paylaşım sesinin (track-level) açık/kapalı olup olmadığı
  const [isScreenAudioEnabled, setIsScreenAudioEnabled] = useState(true);

  const handleStartScreenShare = useCallback(
    async (withAudio: boolean) => {
      try {
        await lk.startScreenShare(withAudio);
        setIsScreenAudioEnabled(true);

        // Kullanıcı audio istemişse ama Chrome "Sekme" yerine başka seçim
        // yaptıysa veya "Sekme sesini paylaş" onayını atladıysa audio track
        // gelmez. Kısa bir gecikmeyle kontrol et, gelmediyse uyar.
        if (withAudio) {
          window.setTimeout(() => {
            const audioTrack = lk.localParticipant?.screenShareAudioTrack;
            if (!audioTrack && typeof window !== "undefined") {
              try {
                window.alert(
                  t(
                    "memeetScreenAudioNotShared",
                    "Ekran sesi paylaşılamadı.\n\nKontrol et:\n• 'Sekme' veya 'Pencere' seçtin mi? ('Tüm Ekran' seçildiyse ses paylaşılmaz.)\n• Tarayıcı izin penceresindeki 'Sekme/Sistem sesini paylaş' onay kutusu işaretli miydi?\n• macOS'ta kulaklık takılıysa sistem sesi paylaşımı OS seviyesinde sınırlanabilir.",
                  ),
                );
              } catch {
                // ignore
              }
            }
          }, 800);
        }
      } catch (error) {
        console.error("Failed to start screen share:", error);
      }
    },
    [lk, t],
  );

  const handleStopScreenShare = useCallback(async () => {
    try {
      await lk.stopScreenShare();
    } catch (error) {
      console.error("Failed to stop screen share:", error);
    }
  }, [lk]);

  const toggleScreenShare = useCallback(() => {
    if (isScreenSharing) {
      void handleStopScreenShare();
      return;
    }
    // Tarayıcı destek tespiti: getDisplayMedia({audio:true}) sadece Chromium-tabanlı
    // tarayıcılarda işlevsel. Safari ve Firefox audio constraint'ini sessizce drop
    // eder → prompt'u sormak yanıltıcı. Sadece destekleyen tarayıcılarda sor.
    let supportsScreenAudio = false;
    try {
      if (typeof navigator !== "undefined") {
        const ua = navigator.userAgent || "";
        const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
        const isFirefox = /firefox|fxios/i.test(ua);
        supportsScreenAudio = !isSafari && !isFirefox;
      }
    } catch {
      supportsScreenAudio = false;
    }

    let withAudio = false;
    if (supportsScreenAudio) {
      try {
        withAudio = typeof window !== "undefined" &&
          window.confirm(
            t(
              "memeetShareScreenAudioPrompt",
              "Ekran sesini de paylaşmak ister misiniz?\n\nNot: Sesli paylaşımda yalnızca bir SEKME paylaşabilirsiniz (Google Meet ile aynı). Pencere veya Tüm Ekran paylaşmak için bu soruya 'Vazgeç' deyin — sessiz paylaşım başlatılır.",
            ),
          );
      } catch {
        withAudio = false;
      }
    }
    void handleStartScreenShare(withAudio);
  }, [isScreenSharing, handleStartScreenShare, handleStopScreenShare, t]);

  // Ekran sesi track'ini track-level enable/disable ile aç/kapat.
  const toggleScreenShareAudio = useCallback(() => {
    const track = lk.localParticipant?.screenShareAudioTrack;
    if (!track) return;
    const next = !isScreenAudioEnabled;
    track.enabled = next;
    setIsScreenAudioEnabled(next);
  }, [lk.localParticipant?.screenShareAudioTrack, isScreenAudioEnabled]);

  // Paylaşım durduğunda ses durumunu sıfırla
  useEffect(() => {
    if (!isScreenSharing) setIsScreenAudioEnabled(true);
  }, [isScreenSharing]);

  // -------------------------------------------------------------------------
  // Hand raise
  // -------------------------------------------------------------------------
  /**
   * Tepkiyi ekrana dusurur. Ayni anda cok sayida tepki gelirse liste
   * REACTION_MAX_ON_SCREEN ile sinirlanir — kalabalik bir toplantida yuzlerce
   * dugum birden animasyona girerse kare duser.
   */
  const pushFloatingReaction = useCallback((kind: ReactionKind, fromName: string) => {
    reactionSeqRef.current += 1;
    const id = `r${reactionSeqRef.current}_${Date.now()}`;
    // Yatay dagilim: hepsi ayni hizada yukselirse ust uste biniyor.
    const offset = Math.round((Math.random() * 2 - 1) * 90);
    const item: FloatingReaction = { id, kind, fromName, offset };

    setFloatingReactions((prev) => {
      const next = prev.length >= REACTION_MAX_ON_SCREEN ? prev.slice(prev.length - REACTION_MAX_ON_SCREEN + 1) : prev;
      return [...next, item];
    });

    const timer = setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
      reactionTimersRef.current.delete(id);
    }, REACTION_LIFETIME_MS);
    reactionTimersRef.current.set(id, timer);
  }, []);

  /** Tepkiyi yayinlar ve yerelde de gosterir (gonderen kendi tepkisini gorur). */
  const sendReaction = useCallback(
    (kind: ReactionKind) => {
      const now = Date.now();
      if (now - lastReactionAtRef.current < REACTION_MIN_GAP_MS) return;
      lastReactionAtRef.current = now;

      const payload = {
        v: 1,
        type: "reaction",
        kind,
        fromName: effectiveDisplayName,
        ts: now,
      };
      void lk.sendData(JSON.stringify(payload), true).catch(() => undefined);
      pushFloatingReaction(kind, effectiveDisplayName);
      setIsReactionMenuOpen(false);
    },
    [lk, effectiveDisplayName, pushFloatingReaction]
  );

  // Bilesen kalkarken bekleyen zamanlayicilari temizle.
  useEffect(() => {
    return () => {
      reactionTimersRef.current.forEach((timer) => clearTimeout(timer));
      reactionTimersRef.current.clear();
    };
  }, []);

  const broadcastHandState = useCallback(
    (raised: boolean, raisedAt?: number) => {
      // [MEMEET-HANDORDER] ts = elin ILK kalkma zamani (yeniden yayinda sira bozulmasin).
      const payload = {
        v: 1,
        type: "hand-state",
        raised,
        fromName: effectiveDisplayName,
        ts: raised && raisedAt ? raisedAt : Date.now(),
      };
      void lk.sendData(JSON.stringify(payload), true).catch(() => undefined);
    },
    [lk, effectiveDisplayName]
  );

  const clearAutoLowerHandTimeout = useCallback(() => {
    if (!autoLowerHandTimeoutRef.current) return;
    window.clearTimeout(autoLowerHandTimeoutRef.current);
    autoLowerHandTimeoutRef.current = null;
  }, []);

  const clearAutoLowerHandSilenceTimeout = useCallback(() => {
    if (!autoLowerHandSilenceTimeoutRef.current) return;
    window.clearTimeout(autoLowerHandSilenceTimeoutRef.current);
    autoLowerHandSilenceTimeoutRef.current = null;
  }, []);

  const lowerLocalHandIfRaised = useCallback(() => {
    setIsHandRaised((prev) => {
      if (!prev) return prev;
      localHandRaisedRef.current = false;
      localHandRaisedAtRef.current = null; // [MEMEET-HANDORDER]
      setLocalHandRaisedAt(null);
      broadcastHandState(false);
      return false;
    });
  }, [broadcastHandState]);

  const handleToggleHandRaise = useCallback(() => {
    setIsHandRaised((prev) => {
      const nextRaised = !prev;
      if (!nextRaised) {
        clearAutoLowerHandTimeout();
        clearAutoLowerHandSilenceTimeout();
      }
      localHandRaisedRef.current = nextRaised;
      const raisedAt = nextRaised ? Date.now() : null; // [MEMEET-HANDORDER]
      localHandRaisedAtRef.current = raisedAt;
      setLocalHandRaisedAt(raisedAt);
      broadcastHandState(nextRaised, raisedAt ?? undefined);
      if (nextRaised) playHandRaiseChime();
      return nextRaised;
    });
  }, [broadcastHandState, clearAutoLowerHandTimeout, clearAutoLowerHandSilenceTimeout, playHandRaiseChime]);

  // Yeniden bağlanınca el durumunu DAİMA indirilmiş olarak yayınla (idempotent
  // reset). Önceki oturumdan kalma raise hand stale state'ini diğer
  // katılımcılarda temizler.
  const justEnteredCallRef = useRef(false);
  useEffect(() => {
    if (callStatus === "in-call" && !justEnteredCallRef.current) {
      justEnteredCallRef.current = true;
      localHandRaisedRef.current = false;
      localHandRaisedAtRef.current = null; // [MEMEET-HANDORDER]
      setLocalHandRaisedAt(null);
      setIsHandRaised(false);
      broadcastHandState(false);
    } else if (callStatus !== "in-call") {
      justEnteredCallRef.current = false;
    }
  }, [callStatus, broadcastHandState]);

  // Ghost cleanup — ayrılan participant'lar için handRaisedByIdentity haritasından
  // entry'leri temizle. Aksi halde "X kişi el kaldırdı" görünmeye devam eder.
  useEffect(() => {
    setHandRaisedByIdentity((prev) => {
      const liveIdentities = new Set(lk.participants.map((p) => p.identity));
      let changed = false;
      const next = new Map<string, boolean>();
      for (const [id, raised] of prev.entries()) {
        if (liveIdentities.has(id)) {
          next.set(id, raised);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // [MEMEET-HANDORDER] kalkma zamani haritasini da temizle
    setHandRaisedAtByIdentity((prev) => {
      const liveIdentities = new Set(lk.participants.map((p) => p.identity));
      let changed = false;
      const next = new Map<string, number>();
      for (const [id, at] of prev.entries()) {
        if (liveIdentities.has(id)) next.set(id, at);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [lk.participants]);

  // [MEMEET-HANDORDER] 2026-11-05: El durumu tek seferlik bir data mesaji; sonradan katilan ya da
  // yeniden baglanan katilimci kalkik eli HIC gormuyordu (eski WebRTC surumu yeni katilana yeniden
  // gonderiyordu, LiveKit'e geciste kaybolmustu). Yeni bir kimlik gorulunce elim kalkiksa durumu ILK
  // kalkma zamaniyla yeniden yayinla; yeni gelenin dinleyicisi gec kurulabilecegi icin kisa aralikla bir kez daha.
  const knownIdentitiesRef = useRef<Set<string>>(new Set());
  const handResendTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const ids = new Set(lk.participants.map((p) => p.identity));
    let hasNew = false;
    ids.forEach((id) => { if (!knownIdentitiesRef.current.has(id)) hasNew = true; });
    knownIdentitiesRef.current = ids;
    if (!hasNew || callStatus !== "in-call") return;
    if (!localHandRaisedRef.current || !localHandRaisedAtRef.current) return;
    broadcastHandState(true, localHandRaisedAtRef.current);
    if (handResendTimerRef.current) window.clearTimeout(handResendTimerRef.current);
    handResendTimerRef.current = window.setTimeout(() => {
      handResendTimerRef.current = null;
      if (localHandRaisedRef.current && localHandRaisedAtRef.current) broadcastHandState(true, localHandRaisedAtRef.current);
    }, 2500);
  }, [lk.participants, callStatus, broadcastHandState]);
  useEffect(() => () => { if (handResendTimerRef.current) window.clearTimeout(handResendTimerRef.current); }, []);

  // Auto-lower based on speaking detection (uses lk.localParticipant.speaking → audioLevel proxy)
  useEffect(() => {
    const canTrackSpeech = callStatus === "in-call" && isHandRaised && isAudioEnabled;
    const isSpeakingNow = canTrackSpeech && audioLevel > HAND_AUTO_LOWER_SPEAKING_THRESHOLD;

    if (!canTrackSpeech) {
      clearAutoLowerHandTimeout();
      clearAutoLowerHandSilenceTimeout();
      return;
    }

    if (isSpeakingNow) {
      clearAutoLowerHandSilenceTimeout();
      if (autoLowerHandTimeoutRef.current) return;

      autoLowerHandTimeoutRef.current = window.setTimeout(() => {
        autoLowerHandTimeoutRef.current = null;
        clearAutoLowerHandSilenceTimeout();
        lowerLocalHandIfRaised();
      }, HAND_AUTO_LOWER_DELAY_MS);
      return;
    }

    if (!autoLowerHandTimeoutRef.current || autoLowerHandSilenceTimeoutRef.current) return;

    autoLowerHandSilenceTimeoutRef.current = window.setTimeout(() => {
      autoLowerHandSilenceTimeoutRef.current = null;
      clearAutoLowerHandTimeout();
    }, HAND_AUTO_LOWER_SILENCE_GRACE_MS);
  }, [
    callStatus,
    isHandRaised,
    isAudioEnabled,
    audioLevel,
    clearAutoLowerHandTimeout,
    clearAutoLowerHandSilenceTimeout,
    lowerLocalHandIfRaised,
  ]);

  useEffect(() => {
    return () => {
      clearAutoLowerHandTimeout();
      clearAutoLowerHandSilenceTimeout();
    };
  }, [clearAutoLowerHandTimeout, clearAutoLowerHandSilenceTimeout]);

  // -------------------------------------------------------------------------
  // Mic/camera toggles — prejoin'da preview stream üstünde, in-call'da LiveKit'te
  // -------------------------------------------------------------------------
  // Bağlantı sonrası yayın hatası (hook'tan): odada kal, kullanıcıya SÖYLE (2026-10-01).
  useEffect(() => {
    const err = lk.mediaError;
    if (!err) return;
    if (err.kind === "audio" && (err.name === "NotAllowedError" || err.name === "SecurityError")) setMicPermissionDenied(true);
    showMuteNotice("error", mediaErrorText(err.kind, err.name));
    lk.clearMediaError();
  }, [lk.mediaError, lk, mediaErrorText, showMuteNotice]);

  const handleToggleAudio = useCallback(() => {
    if (callStatus === "idle") {
      const next = !prejoinAudioEnabled;
      setPrejoinAudioEnabled(next);
      const s = prejoinStreamRef.current;
      if (s) s.getAudioTracks().forEach((t) => (t.enabled = next));
      return;
    }
    // Kendi mic toggle'ımızı işaretle — TrackMuted dinleyicisi bunu moderatör
    // force-mute'undan ayırt etmek için kullanır (yanlış "kapatıldı" toast'ı önlenir).
    selfMicToggleAtRef.current = Date.now();
    void lk.toggleMic().catch((error) => console.error("Failed to toggle mic:", error));
  }, [callStatus, prejoinAudioEnabled, lk]);

  const handleToggleVideo = useCallback(() => {
    if (callStatus === "idle") {
      const next = !prejoinVideoEnabled;
      setPrejoinVideoEnabled(next);
      const s = prejoinStreamRef.current;
      if (s) s.getVideoTracks().forEach((t) => (t.enabled = next));
      return;
    }
    void lk.toggleCamera().catch((error) => console.error("Failed to toggle camera:", error));
  }, [callStatus, prejoinVideoEnabled, lk]);

  // Call duration timer
  useEffect(() => {
    let interval: number | undefined;
    if (callStatus === "in-call") {
      interval = window.setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStatus]);

  // -------------------------------------------------------------------------
  // Grid participants assembly (preserves split camera/screen tile behavior)
  // -------------------------------------------------------------------------
  const gridParticipants = useMemo<Participant[]>(() => {
    const remotes: Participant[] = Array.from(remoteParticipants.values()).flatMap((p) => {
      const stream = remoteStreams.get(p.id) || null;
      const audioTracks = stream?.getAudioTracks?.() || [];
      const videoTracks = stream?.getVideoTracks?.() || [];

      // If a remote stream carries multiple video tracks (camera + screen),
      // split them into separate tiles.
      if (p.isScreenSharing && videoTracks.length > 1) {
        const lkRemote = lk.participants.find((rp) => rp.identity === p.id);
        const screenTrackId = lkRemote?.screenShareTrack?.id || null;
        const screenTrack =
          (screenTrackId ? videoTracks.find((track) => track.id === screenTrackId) : undefined) ??
          pickLikelyScreenTrack(videoTracks) ??
          null;
        const cameraTrack = videoTracks.find((track) => track !== screenTrack) ?? null;

        const camTracks: MediaStreamTrack[] = [];
        if (cameraTrack) camTracks.push(cameraTrack);
        if (audioTracks[0]) camTracks.push(audioTracks[0]);
        const camStream = getCachedStream(`tile:${p.id}-cam`, camTracks);

        const tiles: Participant[] = [
          {
            id: `${p.id}-cam`,
            name: p.displayName,
            userId: p.lodosUserId,
            identity: p.id,
            stream: camStream,
            avatarUrl: p.avatarUrl,
            isAudioEnabled: p.isAudioEnabled && !!audioTracks.length,
            isVideoEnabled: p.isVideoEnabled && !!cameraTrack,
            isScreenSharing: false,
            isHandRaised: p.isHandRaised,
            handRaiseOrder: p.isHandRaised ? handOrderByIdentity.get(p.id) : undefined, // [MEMEET-HANDORDER]
            isLocal: false,
          },
        ];

        if (screenTrack) {
          // Ekran sesini de stream'e EKLE — Google Meet stili: ekran tile'ı kendi
          // audio track'iyle gelir, subscriber tarafında <audio>'ya attach edilir,
          // hoparlöre/kulaklığa normal şekilde çıkar. Aksi halde audio track yetim
          // kalır ve hiç oynatılmaz.
          const screenAudioTrack = lkRemote?.screenShareAudioTrack || null;
          const screenTileTracks: MediaStreamTrack[] = [screenTrack];
          if (screenAudioTrack) screenTileTracks.push(screenAudioTrack);
          const screenStream = getCachedStream(`tile:${p.id}-screen`, screenTileTracks);
          tiles.push({
            id: `${p.id}-screen`,
            name: `${p.displayName} (${t("memeetShareScreen", "Ekranı paylaş")})`,
            userId: p.lodosUserId,
            identity: p.id,
            stream: screenStream,
            avatarUrl: p.avatarUrl,
            isAudioEnabled: !!screenAudioTrack,
            isVideoEnabled: true,
            isScreenSharing: true,
            isHandRaised: false,
            isLocal: false,
          });
        }

        return tiles;
      }

      const hasVideoTrack = !!videoTracks.length;
      const hasAudioTrack = !!audioTracks.length;
      const effectiveScreenSharing = p.isScreenSharing && hasVideoTrack;

      return [
        {
          id: p.id,
          name: p.displayName,
          userId: p.lodosUserId,
          identity: p.id,
          stream,
          avatarUrl: p.avatarUrl,
          isAudioEnabled: p.isAudioEnabled && hasAudioTrack,
          isVideoEnabled: (p.isVideoEnabled || effectiveScreenSharing) && hasVideoTrack,
          isScreenSharing: effectiveScreenSharing,
          isHandRaised: p.isHandRaised,
          handRaiseOrder: p.isHandRaised ? handOrderByIdentity.get(p.id) : undefined, // [MEMEET-HANDORDER]
          isLocal: false,
        },
      ];
    });

    // Always keep local camera tile visible.
    const localCameraTile: Participant = {
      id: "local",
      name: effectiveDisplayName,
      userId: lodosUserId,
      stream: localStream || null,
      isAudioEnabled,
      isVideoEnabled,
      isScreenSharing: false,
      isHandRaised,
      handRaiseOrder: isHandRaised ? handOrderByIdentity.get(localIdentity || "local") : undefined, // [MEMEET-HANDORDER]
      isLocal: true,
      avatarUrl,
    };

    // Local screen share tile (LiveKit publishes screen as a separate track on
    // localParticipant; we surface it here). Audio track'i de stream'e ekliyoruz
    // — local audio her halükarda muted render edilir (echo önlemek için), ama
    // tutarlılık için Participant.stream content'ini doğru tutalım.
    const localScreenTrack = lk.localParticipant?.screenShareTrack || null;
    const localScreenAudioTrack = lk.localParticipant?.screenShareAudioTrack || null;
    const localScreenTileTracks: MediaStreamTrack[] = [];
    if (localScreenTrack) localScreenTileTracks.push(localScreenTrack);
    if (localScreenAudioTrack) localScreenTileTracks.push(localScreenAudioTrack);
    const localScreenStream = localScreenTileTracks.length ? getCachedStream("local-screen", localScreenTileTracks) : null;
    const localScreenTile: Participant[] = isScreenSharing && localScreenStream
      ? [
          {
            id: "local-screen",
            name: `${effectiveDisplayName} (${t("memeetShareScreen", "Ekranı paylaş")})`,
            userId: lodosUserId,
            stream: localScreenStream,
            isAudioEnabled: false,
            isVideoEnabled: true,
            isScreenSharing: true,
            isHandRaised: false,
            isLocal: true,
            avatarUrl,
          },
        ]
      : [];

    return [...remotes, ...localScreenTile, localCameraTile];
  }, [
    remoteParticipants,
    remoteStreams,
    localStream,
    isAudioEnabled,
    isVideoEnabled,
    isHandRaised,
    handOrderByIdentity,
    localIdentity,
    effectiveDisplayName,
    avatarUrl,
    isScreenSharing,
    t,
    lodosUserId,
    lk.localParticipant?.screenShareTrack,
    lk.participants,
  ]);

  // -------------------------------------------------------------------------
  // Misc UI helpers
  // -------------------------------------------------------------------------
  const formatDuration = (seconds: number) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusText = () => {
    switch (callStatus) {
      case "idle":
        return t("memeetReady", "Hazır");
      case "connecting":
        return t("memeetConnecting", "Bağlanıyor...");
      case "in-call":
        return formatDuration(callDuration);
      case "ended":
        return t("memeetCallEnded", "Görüşme bitti");
      default:
        return "";
    }
  };

  const sharePath = useMemo(() => `/memeet/${encodeURIComponent(roomId)}`, [roomId]);

  const shareUrl = useMemo(() => {
    try {
      if (getShareUrl) return String(getShareUrl(roomId) || "");
    } catch {
      // ignore
    }
    try {
      if (typeof window === "undefined") return sharePath;
      return `${window.location.origin}${sharePath}`;
    } catch {
      return sharePath;
    }
  }, [getShareUrl, roomId, sharePath]);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyRoomId = useCallback(async () => {
    // navigator.clipboard güvenli-bağlam yoksa tanımsız/reject olabilir →
    // execCommand textarea fallback'i ekle; "copied" sadece başarıda gösterilsin.
    const text = shareUrl || roomId;
    if (!text) return;
    let ok = false;
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function" &&
        (typeof window === "undefined" || window.isSecureContext !== false)
      ) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      // fallback'e düş
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.width = "1px";
        ta.style.height = "1px";
        ta.style.opacity = "0";
        ta.style.pointerEvents = "none";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, text.length);
        ok = !!(document.execCommand && document.execCommand("copy"));
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  }, [roomId, shareUrl]);

  const handleBack = useCallback(() => {
    if (callStatus === "idle" || callStatus === "ended" || callStatus === "connecting") {
      void lk.disconnect().catch(() => undefined);
      onExitRoom?.();
      return;
    }
    void handleEndCall();
  }, [callStatus, onExitRoom, handleEndCall, lk]);

  // The prejoin "Join" button — kullanıcı kamera/mic ayarını yaptıktan sonra
  // LiveKit'e CONNECT burada başlar. Prejoin preview stream'i kapanır.
  const joinCall = useCallback(() => {
    if (hasConnectedRef.current || !roomId || !token) return;
    hasConnectedRef.current = true;
    setCallStatus("connecting");
    setJoinError("");
    meetingStartedAtRef.current = Date.now();
    stopPrejoinStream();
    lk.connect(roomId, {
      displayName: encodeIdentityNickname(effectiveDisplayName, avatarUrl, lodosUserId),
      audio: prejoinAudioEnabled,
      video: prejoinVideoEnabled,
      audioDeviceId: selectedAudioInput || undefined,
      videoDeviceId: selectedVideoInput || undefined,
    })
      .then(() => setCallStatus("in-call"))
      .catch((err) => {
        // 2026-10-01: Buraya artık yalnız token/bağlantı hataları düşer (yayın hataları
        // hook içinde mediaError'a gider). Yarım kalmış oda kapatılır ki host kişiyi
        // "içeride" görmesin, ve hata lobide GÖSTERİLİR.
        console.error("[LiveKit] connect failed:", err);
        hasConnectedRef.current = false;
        void lk.disconnect().catch(() => undefined);
        const msg = String((err as { message?: string })?.message || "");
        if (/token_endpoint_|token_failed/.test(msg)) {
          setJoinError(t("memeetJoinErrorToken", "Toplantı sunucusuna erişilemedi. Oturumunuz sona ermiş olabilir; sayfayı yenileyip tekrar deneyin."));
        } else {
          setJoinError(t("memeetJoinErrorConnect", "Bağlantı kurulamadı. Ağınızı kontrol edip tekrar deneyin."));
        }
        setCallStatus("idle");
      });
  }, [roomId, token, lk, t, effectiveDisplayName, avatarUrl, lodosUserId, prejoinAudioEnabled, prejoinVideoEnabled, selectedAudioInput, selectedVideoInput, stopPrejoinStream]);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  // ── Yerel ses kontrolleri + kısayollar (2026-10-14) ───────────────────────
  // Çalan SoundPad sesi de anında güncellensin
  useEffect(() => {
    const a = soundboardAudioRef.current;
    if (a) a.volume = isDeafened ? 0 : Math.max(0, Math.min(1, memeetPrefs.prefs.soundpadVolume));
  }, [isDeafened, memeetPrefs.prefs.soundpadVolume]);

  const volumeControls = useMemo(() => ({
    get: memeetPrefs.getParticipantVolume,
    set: memeetPrefs.setParticipantVolume,
    deafened: isDeafened,
    labels: {
      volume: t("memeetParticipantVolume", "Ses seviyesi"),
      muteForMe: t("memeetMuteForMe", "Benim için sustur"),
      unmuteForMe: t("memeetUnmuteForMe", "Benim için sesi aç"),
      onlyForYou: t("memeetOnlyForYou", "Bu ayar yalnız sizi etkiler; diğerleri değişiklik görmez."),
    },
  }), [memeetPrefs.getParticipantVolume, memeetPrefs.setParticipantVolume, isDeafened, t]);

  const soundboardSounds = useMemo(
    () => (soundboardConfig.sounds as SoundboardItem[]).map((s) => ({ id: s.id, label: s.title })),
    []
  );

  // Kısayol eylemleri her render'da güncel tutulur (dinleyici yeniden kurulmadan)
  const shortcutActionsRef = useRef<Record<string, () => void>>({});
  shortcutActionsRef.current = {
    toggleMic: handleToggleAudio,
    toggleCamera: handleToggleVideo,
    toggleSpeaker: () => setIsDeafened((d) => !d),
    toggleScreenShare,
    toggleHand: handleToggleHandRaise,
    toggleChat,
    toggleSoundpad: toggleSoundboard,
    openSettings: () => setIsDeviceSettingsOpen((o) => !o),
    leaveCall: requestEndCall,
  };
  const lkRef = useRef(lk);
  lkRef.current = lk;
  const soundClickRef = useRef(handleSoundboardClick);
  soundClickRef.current = handleSoundboardClick;
  const pttActiveRef = useRef(false);

  useEffect(() => {
    if (callStatus !== "in-call") return;
    const byCombo = new Map<string, string>();
    for (const [action, combo] of Object.entries(memeetPrefs.shortcuts)) if (combo) byCombo.set(combo, action);
    const pttKey = (memeetPrefs.shortcuts.pushToTalk || "").split("+").pop() || "";
    const micOff = () => {
      if (!pttActiveRef.current) return;
      pttActiveRef.current = false;
      if (lkRef.current.isMicEnabled) {
        selfMicToggleAtRef.current = Date.now();
        void lkRef.current.toggleMic().catch(() => undefined);
      }
    };
    const onDown = (e: KeyboardEvent) => {
      if ((window as any).__memeetShortcutCapture) return;
      const combo = comboFromEvent(e, memeetPrefs.platform);
      if (!combo) return;
      const action = byCombo.get(combo);
      if (!action) return;
      if (isTypingTarget(e.target) && !comboAllowedWhileTyping(combo)) return;
      e.preventDefault();
      e.stopPropagation();
      if (action === "pushToTalk") {
        // Bas-konuş: basılı tutarken mikrofon açık; bırakınca kapanır (zaten açıksa dokunulmaz)
        if (!e.repeat && !pttActiveRef.current && !lkRef.current.isMicEnabled) {
          pttActiveRef.current = true;
          selfMicToggleAtRef.current = Date.now();
          void lkRef.current.toggleMic().catch(() => { pttActiveRef.current = false; });
        }
        return;
      }
      if (e.repeat) return;
      if (action.startsWith("sound:")) { soundClickRef.current(action.slice(6)); return; }
      shortcutActionsRef.current[action]?.();
    };
    const onUp = (e: KeyboardEvent) => {
      if (!pttActiveRef.current) return;
      if (e.code === pttKey || /^(Meta|Control|Alt|Shift|OS)/.test(e.code)) micOff();
    };
    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("blur", micOff);
    return () => {
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("blur", micOff);
      micOff();
    };
  }, [callStatus, memeetPrefs.shortcuts, memeetPrefs.platform]);

  // Masaüstü (Electron): seçilen eylemler uygulama arka plandayken de çalışır (globalShortcut)
  const globalShortcutsSupported = typeof window !== "undefined" && typeof (window as any).electronAPI?.memeetSetGlobalShortcuts === "function";
  useEffect(() => {
    const api = (window as any).electronAPI;
    if (!api || typeof api.memeetSetGlobalShortcuts !== "function") return;
    if (callStatus !== "in-call") { api.memeetSetGlobalShortcuts([]); return; }
    const list = memeetPrefs.prefs.globalShortcuts
      .map((action) => ({ action, accelerator: toElectronAccelerator(memeetPrefs.shortcuts[action] || "") }))
      .filter((x) => !!x.accelerator);
    api.memeetSetGlobalShortcuts(list);
    const off = typeof api.onMemeetGlobalShortcut === "function"
      ? api.onMemeetGlobalShortcut((action: string) => shortcutActionsRef.current[action]?.())
      : null;
    return () => {
      api.memeetSetGlobalShortcuts([]);
      if (typeof off === "function") off();
    };
  }, [callStatus, memeetPrefs.prefs.globalShortcuts, memeetPrefs.shortcuts]);

  // Ayarlar her açıldığında tercihleri sunucudan tazele (başka cihazdaki değişiklik görünsün)
  const reloadPrefs = memeetPrefs.reload;
  useEffect(() => { if (isDeviceSettingsOpen) reloadPrefs(); }, [isDeviceSettingsOpen, reloadPrefs]);

  const prejoinParticipants = useMemo<PreConnectParticipant[]>(() => {
    // Idle (gerçek pre-join) sırasında lk.participants boştur; backend'den çekilen
    // preConnectParticipants kullanılır. Bağlanınca (connecting/connected) canlı
    // lk.participants türevine geri düşülür.
    const source: PreConnectParticipant[] =
      callStatus === "idle"
        ? preConnectParticipants
        : Array.from(remoteParticipants.values()).map((p) => ({
            id: p.id,
            displayName: p.displayName,
            avatarUrl: p.avatarUrl,
            lodosUserId: p.lodosUserId,
          }));
    return source
      .filter((p) => p.id && p.displayName)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [callStatus, preConnectParticipants, remoteParticipants]);
  const prejoinVisibleParticipants = useMemo(
    () => prejoinParticipants.slice(0, MAX_PREJOIN_PARTICIPANTS),
    [prejoinParticipants]
  );
  const prejoinExtraCount = prejoinParticipants.length - prejoinVisibleParticipants.length;

  // -------------------------------------------------------------------------
  // Chat send
  // -------------------------------------------------------------------------
  const sendChatMessage = useCallback(() => {
    const trimmed = String(chatDraft || "").trim();
    if (!trimmed && !pendingAttachment) return;

    const now = Date.now();
    const message: ChatMessage = {
      id: makeChatId(),
      text: trimmed,
      ts: now,
      fromId: localIdentity || "local",
      fromName: effectiveDisplayName,
      fromAvatarUrl: avatarUrl,
      isLocal: true,
      attachment: pendingAttachment || undefined,
    };

    appendChatMessage(message);
    setChatDraft("");
    setPendingAttachment(null);

    const wireMessage: ChatWireMessageV1 = {
      v: 1,
      type: "chat-message",
      id: message.id,
      ts: message.ts,
      text: message.text,
      from: {
        name: message.fromName,
        avatarUrl: message.fromAvatarUrl,
      },
      attachment: message.attachment,
    };

    void lk.sendData(JSON.stringify(wireMessage), true).catch((error) => {
      console.error("Failed to send chat message:", error);
    });
  }, [appendChatMessage, avatarUrl, chatDraft, effectiveDisplayName, localIdentity, pendingAttachment, lk]);

  const handleChatSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      sendChatMessage();
    },
    [sendChatMessage]
  );

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }, []);

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          if (file.size > MAX_ATTACHMENT_SIZE) {
            alert(t("memeetFileTooLarge", "Dosya çok büyük. Maksimum 2MB."));
            return;
          }
          const base64 = await fileToBase64(file);
          setPendingAttachment({
            type: file.type,
            name: file.name || `image-${Date.now()}.png`,
            data: base64,
            size: file.size,
          });
          break;
        }
      }
    },
    [fileToBase64, t]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_ATTACHMENT_SIZE) {
        alert(t("memeetFileTooLarge", "Dosya çok büyük. Maksimum 2MB."));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const base64 = await fileToBase64(file);
      setPendingAttachment({
        type: file.type,
        name: file.name,
        data: base64,
        size: file.size,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [fileToBase64, t]
  );

  const handleRemoveAttachment = useCallback(() => {
    setPendingAttachment(null);
  }, []);

  const formatChatTime = useCallback(
    (ts: number) => {
      try {
        return new Date(ts).toLocaleTimeString(language || undefined, {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "";
      }
    },
    [language]
  );

  return (
    <div className="memeet-video-call" data-transport="livekit">
      {/* LiveKit branch'inde mirror tutarsızlığını kapat — hepsi gerçek pozisyon.
          Eski WebRTC akışı (data-transport yok) bundan etkilenmez. */}
      {/* Self-view aynasi BILEREK birakildi: eskiden burada ayna iptal
          ediliyordu ve kullanici kendini "ters" goruyordu. Zoom/Meet/Teams
          self-view'i aynalar. Giden akis etkilenmez. */}
      {callStatus === "idle" || callStatus === "connecting" ? (
        <div className="memeet-prejoin">
          <div className="prejoin-preview">
            {((callStatus === "idle" ? prejoinStream : localStream) && isVideoEnabled) ? (
              <video ref={previewVideoRef} autoPlay playsInline muted className="prejoin-video" />
            ) : (
              <div className="prejoin-avatar">
                <AvatarCircle name={effectiveDisplayName} avatarUrl={avatarUrl} />
              </div>
            )}
            <div className="tile-name-badge">{effectiveDisplayName}</div>
          </div>

          <div className="prejoin-panel">
            <h2>
              {callStatus === "connecting"
                ? t("memeetConnecting", "Bağlanıyor...")
                : t("memeetReadyToJoin", "Katılmaya hazır mısınız?")}
            </h2>
            <p>{t("memeetJoinWithoutVideoHint", "Kameranız kapalıysa toplantıya görüntüsüz katılabilirsiniz.")}</p>
            {callStatus === "idle" ? (
              <div className="prejoin-vfx">
                <VideoEffectsPicker value={videoEffect} onChange={changeVideoEffect} t={t} busy={vfxBusy} />
              </div>
            ) : null}

            {prejoinParticipants.length > 0 && (
              <div className="prejoin-participants">
                <span className="prejoin-participants-label">{t("memeetPeopleInside", "İçeridekiler")}</span>
                <div className="prejoin-participants-row">
                  <div className="prejoin-participants-stack">
                    {prejoinVisibleParticipants.map((participant) => {
                      const isHost = Boolean(
                        hostUserId && participant.lodosUserId && String(participant.lodosUserId) === hostUserId
                      );
                      return (
                        <div
                          key={participant.id}
                          className={`prejoin-participant-avatar ${isHost ? "is-host" : ""}`}
                          title={
                            isHost
                              ? `${participant.displayName} — ${t("memeetHostBadge", "Yönetici")}`
                              : participant.displayName
                          }
                          aria-label={
                            isHost
                              ? `${participant.displayName} — ${t("memeetHostBadge", "Yönetici")}`
                              : participant.displayName
                          }
                        >
                          <AvatarCircle name={participant.displayName} avatarUrl={participant.avatarUrl} />
                          {isHost && (
                            <span className="prejoin-participant-host-dot" aria-hidden="true">
                              <PrejoinHostIcon />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {prejoinExtraCount > 0 && (
                    <span className="prejoin-participants-more">
                      {t("memeetMorePeople", `${prejoinExtraCount} Kişi daha`, {
                        count: String(prejoinExtraCount),
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="prejoin-controls">
              {/* Etiketli kontroller (2026-10-01): kullanıcılar mikrofonun açık mı
                  kapalı mı olduğunu ikondan anlayamıyordu. Durum metni + belirgin
                  kırmızı kapalı hali; gürültü engelleme ayrı (soluk) stil. */}
              <div className="memeet-ctrl">
                <button
                  className={`icon-button large ${!isAudioEnabled ? "off" : "on"}`}
                  onClick={handleToggleAudio}
                  aria-pressed={isAudioEnabled}
                  title={
                    isAudioEnabled
                      ? t("memeetTurnOffMic", "Mikrofonu kapat")
                      : t("memeetTurnOnMic", "Mikrofonu aç")
                  }
                >
                  {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                </button>
                <span className={`memeet-ctrl-label ${!isAudioEnabled ? "off" : ""}`}>
                  {micPermissionDenied
                    ? t("memeetMicNoPermission", "Mikrofon izni yok")
                    : isAudioEnabled
                      ? t("memeetMicOn", "Mikrofon açık")
                      : t("memeetMicOff", "Mikrofon kapalı")}
                </span>
              </div>

              <div className="memeet-ctrl">
                <button
                  type="button"
                  className={`icon-button large ${!lk.noiseSuppressionEnabled ? "dim" : ""}`}
                  aria-pressed={lk.noiseSuppressionEnabled}
                  title={lk.noiseSuppressionEnabled ? t("memeetNoiseSuppressionOn", "Gürültü engelleme açık") : t("memeetNoiseSuppressionOff", "Gürültü engelleme kapalı")}
                  onClick={() => void lk.setNoiseSuppression(!lk.noiseSuppressionEnabled)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 12h3l3-7 4 14 3-7h5" />
                    {!lk.noiseSuppressionEnabled && <line x1="3" y1="3" x2="21" y2="21" />}
                  </svg>
                </button>
                <span className="memeet-ctrl-label">{t("memeetNoiseLabel", "Gürültü engelleme")}</span>
              </div>

              <div className="memeet-ctrl">
                <button
                  className={`icon-button large ${!isVideoEnabled ? "off" : "on"}`}
                  onClick={handleToggleVideo}
                  aria-pressed={isVideoEnabled}
                  title={
                    isVideoEnabled
                      ? t("memeetTurnOffCamera", "Kamerayı kapat")
                      : t("memeetTurnOnCamera", "Kamerayı aç")
                  }
                >
                  {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
                </button>
                <span className={`memeet-ctrl-label ${!isVideoEnabled ? "off" : ""}`}>
                  {isVideoEnabled ? t("memeetCameraOn", "Kamera açık") : t("memeetCameraOff", "Kamera kapalı")}
                </span>
              </div>

              <div className="memeet-ctrl">
                <button
                  type="button"
                  className="icon-button large"
                  onClick={() => setIsDeviceSettingsOpen(true)}
                  title={t("memeetSettings", "Ayarlar")}
                  aria-label={t("memeetSettings", "Ayarlar")}
                >
                  <SettingsIcon />
                </button>
                <span className="memeet-ctrl-label">{t("memeetSettings", "Ayarlar")}</span>
              </div>
            </div>

            {prejoinNotice ? (
              <div className={`prejoin-notice ${micPermissionDenied ? "critical" : "warning"}`} role="status">
                {prejoinNotice}
              </div>
            ) : null}
            {joinError ? (
              <div className="prejoin-notice critical" role="alert">
                {joinError}
              </div>
            ) : null}

            <div className="prejoin-actions">
              <button className="meet-button primary" onClick={joinCall} disabled={callStatus === "connecting"}>
                {callStatus === "connecting"
                  ? t("memeetConnecting", "Bağlanıyor...")
                  : joinError
                    ? t("memeetRetryJoin", "Tekrar dene")
                    : t("memeetJoin", "Katıl")}
              </button>
              <button className="meet-button ghost" onClick={handleBack} disabled={callStatus === "connecting"} type="button">
                {t("memeetCancel", "Vazgeç")}
              </button>
            </div>

            <div className="prejoin-status">
              <span>
                {callStatus === "connecting"
                  ? t("memeetConnectingToMeeting", "Toplantıya bağlanılıyor...")
                  : getStatusText()}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="memeet-incall">
          <div className="memeet-call-topbar">
            <div className="topbar-left">
              <span className="topbar-room-name" title={roomId}>
                {roomId}
              </span>
              <span className="topbar-participant-count">
                <UsersIcon />
                <span className="topbar-participant-count-text">
                  {t(
                    "memeetParticipantCount",
                    `${remoteParticipants.size + 1} participants`,
                    { count: String(remoteParticipants.size + 1) }
                  )}
                </span>
              </span>
            </div>
          </div>
          {videoRecording ? (
            <div className="memeet-vidrec-indicator" role="status" aria-live="polite">
              <span className="memeet-vidrec-indicator-dot" />
              {t("memeetRecording", "Kayıt ediliyor")}
            </div>
          ) : null}
          {videoRecConsentOpen
            ? createPortal(
                <div
                  className="memeet-vidrec-consent-backdrop"
                  role="dialog"
                  aria-modal="true"
                  onClick={() => setVideoRecConsentOpen(false)}
                >
                  <div className="memeet-vidrec-consent" onClick={(e) => e.stopPropagation()}>
                    <div className="memeet-vidrec-consent-ico" aria-hidden="true">
                      <RecDotIcon />
                    </div>
                    <p className="memeet-vidrec-consent-text">
                      {t(
                        "memeetVideoRecordConsent",
                        "Bu toplantı video olarak kaydedilecek ve katılımcılar bilgilendirilecek. Devam edilsin mi?"
                      )}
                    </p>
                    <div className="memeet-vidrec-consent-actions">
                      <button
                        type="button"
                        className="memeet-vidrec-consent-cancel"
                        onClick={() => setVideoRecConsentOpen(false)}
                      >
                        {t("memeetCancel", "Vazgeç")}
                      </button>
                      <button
                        type="button"
                        className="memeet-vidrec-consent-confirm"
                        onClick={confirmStartVideoRecording}
                        disabled={videoRecBusy}
                      >
                        {t("memeetRecord", "Kaydet")}
                      </button>
                    </div>
                  </div>
                  <style>{MEMEET_REC_CSS}</style>
                </div>,
                document.body
              )
            : null}
          <div className="incall-content">
            <div className="stage">
              <ParticipantsGrid
                participants={gridParticipants}
                audioOutputDeviceId={selectedAudioOutput || undefined}
                handRaisedLabel={t("memeetHandRaised", "El kaldırdı")}
                pinLabel={t("memeetFullscreen", "Tam ekran")}
                unpinLabel={t("memeetExitFullscreen", "Tam ekrandan çık")}
                getOverflowLabel={(count) =>
                  count === 1
                    ? t("memeetOverflowPerson", `${count} Kişi`, { count: String(count) })
                    : t("memeetOverflowPeople", `${count} Kişi`, { count: String(count) })
                }
                participantActionLabel={
                  onSendDmOnSocialHive ? t("memeetSendDmOnSocialHive", "Send DM on Social Hive") : undefined
                }
                onParticipantActionClick={onSendDmOnSocialHive ? handleParticipantActionClick : undefined}
                hostUserId={hostUserId || undefined}
                hostBadgeLabel={t("memeetHostBadge", "Yönetici")}
                canMuteParticipants={isLocalUserHost && Boolean(onMuteParticipant)}
                onMuteParticipant={isLocalUserHost && onMuteParticipant ? handleMuteParticipant : undefined}
                muteLabel={t("memeetMuteParticipant", "Mikrofonu kapat")}
                unmuteLabel={t("memeetUnmuteParticipant", "Mikrofonu aç")}
                volumeControls={volumeControls}
              />
              {handQueue.length > 0 ? (
                // [MEMEET-HANDORDER] el kaldiranlar sirasi — kisi ekranda olmasa da gorunur
                <div className="memeet-hand-queue" aria-live="polite">
                  <div className="memeet-hand-queue-title">
                    <HandRaiseIcon />
                    <span>{t("memeetRaisedHands", "El kaldıranlar")} ({handQueue.length})</span>
                  </div>
                  <ol className="memeet-hand-queue-list">
                    {handQueue.slice(0, 8).map((h, i) => (
                      <li key={h.identity} className={h.isLocal ? "is-local" : undefined}>
                        <span className="memeet-hand-queue-no">{i + 1}</span>
                        <span className="memeet-hand-queue-name">{h.name}{h.isLocal ? ` (${t("memeetYou", "Sen")})` : ""}</span>
                      </li>
                    ))}
                  </ol>
                  {handQueue.length > 8 ? <div className="memeet-hand-queue-more">+{handQueue.length - 8}</div> : null}
                </div>
              ) : null}
              {floatingReactions.length > 0 ? (
                <div className="memeet-reactions-layer" aria-live="polite">
                  {floatingReactions.map((r) => (
                    <div
                      key={r.id}
                      className="memeet-reaction-float"
                      style={{ left: `calc(50% + ${r.offset}px)` }}
                    >
                      <span className={`bubble kind-${r.kind}`}>
                        <ReactionIcon kind={r.kind} />
                      </span>
                      <span className="who">{r.fromName}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <aside className={`memeet-soundboard-panel ${isSoundboardOpen ? "open" : ""}`} aria-hidden={!isSoundboardOpen}>
              <div className="soundboard-panel-header">
                <span className="soundboard-panel-title">{t("memeetSoundboard", "Soundboard")}</span>
                <button
                  className="icon-button chat-close"
                  onClick={() => setIsSoundboardOpen(false)}
                  title={t("memeetCloseSoundboard", "Kapat")}
                  type="button"
                >
                  <CloseIcon />
                </button>
              </div>
              <label className="soundboard-volume">
                <span>{t("memeetSoundpadVolume", "Efekt sesi")}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={Math.round(memeetPrefs.prefs.soundpadVolume * 100)}
                  onChange={(e) => memeetPrefs.setSoundpadVolume(Number(e.target.value) / 100)}
                  aria-label={t("memeetSoundpadVolume", "Efekt sesi")}
                />
                <span className="soundboard-volume-val">{Math.round(memeetPrefs.prefs.soundpadVolume * 100)}%</span>
              </label>
              <div className="soundboard-grid">
                {(soundboardConfig.sounds as SoundboardItem[]).map((sound) => (
                  <button
                    key={sound.id}
                    className={`soundboard-btn ${playingSoundId === sound.id ? "playing" : ""}`}
                    onClick={() => handleSoundboardClick(sound.id)}
                    disabled={callStatus !== "in-call"}
                    title={sound.title}
                    type="button"
                  >
                    <SoundboardIcon icon={sound.icon} />
                    <span className="soundboard-btn-label">{sound.title}</span>
                    {playingSoundId === sound.id && <span className="soundboard-playing-indicator" />}
                  </button>
                ))}
              </div>
            </aside>

            <aside className={`memeet-chat-panel ${isChatOpen ? "open" : ""}`} aria-hidden={!isChatOpen}>
              <div className="chat-panel-header">
                <span className="chat-panel-title">{t("memeetChat", "Sohbet")}</span>
                <button
                  className="icon-button chat-close"
                  onClick={() => setIsChatOpen(false)}
                  title={t("memeetCloseChat", "Sohbeti kapat")}
                  type="button"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="chat-messages" ref={chatListRef} onScroll={updateChatScrollLock}>
                {chatMessages.length === 0 ? (
                  <div className="chat-empty">{t("memeetChatEmpty", "Henüz mesaj yok.")}</div>
                ) : (
                  chatMessages.map((m) => {
                    const checklistAdds = (checklistAddsByMessageId[m.id] || []).filter((a) =>
                      workspaceIdSet.has(String(a.workspaceId))
                    );
                    const notesAdd = notesAddedByMessageId[m.id] || null;
                    const preview = truncateChatPreview(m.text);

                    return (
                    <div key={m.id} className={`chat-message ${m.isLocal ? "local" : ""}`}>
                      <div className="chat-avatar">
                        {m.fromAvatarUrl ? (
                          <img src={m.fromAvatarUrl} alt={m.fromName} />
                        ) : (
                          <span>{getInitials(m.fromName)}</span>
                        )}
                      </div>

                      <div className="chat-content">
                        <div className="chat-meta">
                          <span className="chat-name">{m.isLocal ? t("memeetYou", "Siz") : m.fromName}</span>
                          <div className="chat-meta-right">
                            {!m.attachment && (
                              <>
                                <button
                                  type="button"
                                  className="memeet-chat-action-btn"
                                  title={t("memeetAddToChecklist", "Add to checklist")}
                                  aria-label={t("memeetAddToChecklist", "Add to checklist")}
                                  disabled={!canAddToChecklist || isChecklistAddInFlight}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    openChecklistPickerForMessage(m.id);
                                  }}
                                >
                                  {addingChecklistForMessageId === m.id ? (
                                    <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                  ) : (
                                    <ChecklistAddIcon />
                                  )}
                                </button>

                                <button
                                  type="button"
                                  className="memeet-chat-action-btn"
                                  title={t("memeetAddToNotes", "Add to notes")}
                                  aria-label={t("memeetAddToNotes", "Add to notes")}
                                  disabled={!canAddToNotes || isNotesAddInFlight}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleAddToNotes(m.id);
                                  }}
                                >
                                  {isAddingToNotesForMessageId === m.id ? (
                                    <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                  ) : (
                                    <NotesAddIcon />
                                  )}
                                </button>
                              </>
                            )}

                            <span className="chat-time">{formatChatTime(m.ts)}</span>
                          </div>
                        </div>
                        <div className="chat-bubble" onContextMenu={(e) => openChatContextMenu(e, m.id)}>
                          <div className="chat-text">
                            {m.attachment && (
                              <div className="chat-attachment">
                                {m.attachment.type.startsWith("image/") ? (
                                  <img
                                    src={m.attachment.data}
                                    alt={m.attachment.name}
                                    className="chat-image"
                                    onClick={() => setLightboxImage(m.attachment!.data)}
                                  />
                                ) : (
                                  <a
                                    href={m.attachment.data}
                                    download={m.attachment.name}
                                    className="chat-file-link"
                                  >
                                    <FileIcon />
                                    <span className="file-name">{m.attachment.name}</span>
                                    <span className="file-size">({formatFileSize(m.attachment.size)})</span>
                                  </a>
                                )}
                              </div>
                            )}
                            {/* Satir sonlarini BOLMUYORUZ: split("\n") bos satirlari
                                <p></p> yapiyordu, icerigi olmayan blok elemanin satir
                                kutusu olmadigi icin yuksekligi 0 olup paragraf
                                bosluklari kayboluyordu. .chat-text zaten
                                `white-space: pre-wrap` tasidigi icin metni oldugu gibi
                                verince tum satir sonlari korunuyor. Bas/son satir
                                sonlari kirpiliyor ki balonda bos bant olmasin. */}
                            {m.text && (
                              <p>{linkifyText(m.text.replace(/^\n+|\n+$/g, ""))}</p>
                            )}
                            {(() => {
                              const urls = extractUrls(m.text);
                              if (!urls.length) return null;
                              return (
                                <div className="link-preview-list">
                                  {urls.map((u) => {
                                    const pv = linkPreviews[u];
                                    if (!pv) return <LinkPreviewSkeleton key={u} />;
                                    const previewData = "error" in pv ? { title: "", description: "", image: null, url: u } : pv;
                                    const href = ("error" in pv ? u : pv.url) || u;
                                    return <LinkPreviewCard key={u} preview={previewData} href={href} getTranslation={getTranslation} />;
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {checklistAdds.map((a, idx) => (
                          <div
                            key={`${a.workspaceId}-${a.checklistId || a.checklistName}-${idx}`}
                            className="chat-action-hint"
                            title={a.checklistName}
                          >
                            <span className="chat-action-preview">
                              "{truncateChatActionToken(a.preview ? a.preview : m.text)}"
                            </span>{" "}
                            <span className="chat-action-text">
                              {t(
                                "memeetWasAddedIntoChecklistNamed",
                                `was added into checklist named ${a.checklistName}`,
                                { checklistName: a.checklistName }
                              )}
                            </span>
                          </div>
                        ))}

                        {notesAdd ? (
                          <div className="chat-action-hint">
                            <span className="chat-action-preview">"{truncateChatActionToken(m.text)}"</span>{" "}
                            <span className="chat-action-text">
                              {t("memeetWasAddedIntoYourNotes", "was added into your notes")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>

              <form className="chat-compose" onSubmit={handleChatSubmit}>
                {pendingAttachment && (
                  <div className="chat-attachment-preview">
                    {pendingAttachment.type.startsWith("image/") ? (
                      <>
                        <img src={pendingAttachment.data} alt="preview" />
                        <div className="image-info">
                          <span className="file-name">{pendingAttachment.name}</span>
                          <span className="file-meta">{formatFileSize(pendingAttachment.size)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="file-preview">
                        <FileIcon />
                        <span className="file-name">{pendingAttachment.name}</span>
                        <span className="file-size">{formatFileSize(pendingAttachment.size)}</span>
                      </div>
                    )}
                    <button type="button" className="remove-attachment" onClick={handleRemoveAttachment} title={t("memeetRemoveAttachment", "Kaldır")}>
                      <CloseIcon />
                    </button>
                  </div>
                )}
                <div className="chat-input-row">
                  <button
                    type="button"
                    className="icon-button attachment-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={callStatus !== "in-call"}
                    title={t("memeetAttachFile", "Dosya ekle")}
                  >
                    <AttachmentIcon />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                  />
                  <input
                    ref={chatDraftInputRef}
                    value={chatDraft}
                    onChange={(e) => setChatDraft(e.target.value)}
                    onPaste={handlePaste}
                    placeholder={t("memeetChatPlaceholder", "Mesaj yazın")}
                    disabled={callStatus !== "in-call"}
                  />
                  <button
                    className="icon-button chat-send-btn"
                    type="submit"
                    disabled={callStatus !== "in-call" || (!chatDraft.trim() && !pendingAttachment)}
                    title={t("memeetSend", "Gönder")}
                    aria-label={t("memeetSend", "Gönder")}
                  >
                    <SendIcon />
                  </button>
                </div>
              </form>
            </aside>
          </div>

          <div className="controls-bar">
            <div className="controls-left">
              <div className="controls-left-stack">
                <button
                  type="button"
                  className="invite-room-button"
                  onClick={openInviteModal}
                  title={t("memeetInvite", "Invite")}
                >
                  <InviteIcon />
                  <span>{t("memeetInvite", "Invite")}</span>
                </button>
                {summaryApi && roomId ? (
                  <MeetingSummary summaryApi={summaryApi} roomId={roomId} t={t} onRecordingChange={handleRecordingChange} />
                ) : null}
                {canControlVideoRecording ? (
                  videoRecording ? (
                    <button
                      type="button"
                      className="memeet-vidrec-btn stop"
                      onClick={stopVideoRecording}
                      disabled={videoRecBusy || callStatus !== "in-call"}
                      title={t("memeetStopRecording", "Durdur")}
                    >
                      <RecStopIcon />
                      <span>{t("memeetStopRecording", "Durdur")}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="memeet-vidrec-btn start"
                      onClick={requestStartVideoRecording}
                      disabled={videoRecBusy || callStatus !== "in-call"}
                      title={t("memeetRecord", "Kaydet")}
                    >
                      <RecDotIcon />
                      <span>{t("memeetRecord", "Kaydet")}</span>
                    </button>
                  )
                ) : null}
                {recordingActive ? (
                  <div className="memeet-rec-badge" title={t("memeetRecordingOn", "Kayıt yapılıyor")}>
                    <span className="memeet-rec-dot" />
                    {t("memeetRecordingOn", "Kayıt yapılıyor")}
                  </div>
                ) : null}
              </div>
            </div>

            {recordingNotice ? (
              <div className={`memeet-rec-toast ${recordingNotice.on ? "on" : "off"}`}>
                {recordingNotice.on
                  ? `● ${recordingNotice.name || t("memeetSomeone", "Bir katılımcı")} ${t("memeetStartedRecording", "kayıt başlattı")}`
                  : t("memeetRecStopped", "Kayıt durduruldu.")}
              </div>
            ) : null}
            {muteNotice ? (
              <div className={`memeet-mute-toast ${muteNotice.tone}`}>{muteNotice.text}</div>
            ) : null}
            {lk.isReconnecting ? (
              <div className="memeet-conn-banner" role="status" aria-live="polite">
                <span className="memeet-conn-spinner" aria-hidden="true" />
                {t("memeetReconnecting", "Bağlantı yeniden kuruluyor…")}
              </div>
            ) : null}
            {!lk.canPlaybackAudio ? (
              <button type="button" className="memeet-conn-banner clickable" onClick={() => void lk.startAudio()}>
                {t("memeetEnableAudio", "Sesi duymak için tıklayın")}
              </button>
            ) : null}
            {videoRecToast ? (
              <div className={`memeet-mute-toast ${videoRecToast.tone}`}>{videoRecToast.text}</div>
            ) : null}
            <style>{MEMEET_REC_CSS}</style>

            <div className="controls-center-group">
            <div className="controls-center">
            <div className="memeet-reaction-control">
              {isReactionMenuOpen ? (
                <div className="memeet-reaction-menu" role="menu">
                  {REACTION_KINDS.map((r) => (
                    <button
                      key={r.kind}
                      type="button"
                      className="memeet-reaction-choice"
                      role="menuitem"
                      onClick={() => sendReaction(r.kind)}
                      title={t(r.labelKey, r.labelFallback)}
                      aria-label={t(r.labelKey, r.labelFallback)}
                    >
                      <ReactionIcon kind={r.kind} />
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                className={`icon-button large ${isReactionMenuOpen ? "active" : ""}`}
                onClick={() => setIsReactionMenuOpen((v) => !v)}
                disabled={callStatus !== "in-call"}
                aria-expanded={isReactionMenuOpen}
                aria-haspopup="menu"
                title={t("memeetReactions", "Tepkiler")}
              >
                <ReactionIcon kind="thumbsup" />
              </button>
            </div>

            <button
              className={`icon-button large ${isHandRaised ? "active" : ""}`}
              onClick={handleToggleHandRaise}
              disabled={callStatus !== "in-call"}
              title={isHandRaised ? t("memeetLowerHand", "Eli indir") : t("memeetRaiseHand", "El kaldır")}
            >
              <HandRaiseIcon />
            </button>

            {/* Etiketli mikrofon/kamera (2026-10-01): durum metni + belirgin kırmızı
                kapalı hali; gürültü engelleme ayrı (soluk) stilde ki mikrofonla karışmasın. */}
            <div className="memeet-ctrl">
              <button
                className={`icon-button large ${!isAudioEnabled ? "off" : "on"}`}
                onClick={handleToggleAudio}
                disabled={callStatus !== "in-call"}
                aria-pressed={isAudioEnabled}
                title={isAudioEnabled ? t("memeetTurnOffMic", "Mikrofonu kapat") : t("memeetTurnOnMic", "Mikrofonu aç")}
              >
                {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
              </button>
              <span className={`memeet-ctrl-label ${!isAudioEnabled ? "off" : ""}`}>
                {micPermissionDenied && !isAudioEnabled
                  ? t("memeetMicNoPermission", "Mikrofon izni yok")
                  : isAudioEnabled
                    ? t("memeetMicOn", "Mikrofon açık")
                    : t("memeetMicOff", "Mikrofon kapalı")}
              </span>
            </div>

            <div className="memeet-ctrl">
              <button
                type="button"
                className={`icon-button large ${isDeafened ? "off" : "on"}`}
                onClick={() => setIsDeafened((d) => !d)}
                disabled={callStatus !== "in-call"}
                aria-pressed={!isDeafened}
                title={isDeafened ? t("memeetSpeakerTurnOn", "Hoparlörü aç") : t("memeetSpeakerTurnOff", "Hoparlörü kapat (kimseyi duyma)")}
              >
                {isDeafened ? <SpeakerOffCtrlIcon /> : <SpeakerCtrlIcon />}
              </button>
              <span className={`memeet-ctrl-label ${isDeafened ? "off" : ""}`}>
                {isDeafened ? t("memeetSpeakerOff", "Hoparlör kapalı") : t("memeetSpeakerOn", "Hoparlör açık")}
              </span>
            </div>

            <div className="memeet-ctrl">
              <button
                type="button"
                className={`icon-button large ${!lk.noiseSuppressionEnabled ? "dim" : ""}`}
                aria-pressed={lk.noiseSuppressionEnabled}
                disabled={callStatus !== "in-call"}
                title={lk.noiseSuppressionEnabled ? t("memeetNoiseSuppressionOn", "Gürültü engelleme açık") : t("memeetNoiseSuppressionOff", "Gürültü engelleme kapalı")}
                onClick={() => void lk.setNoiseSuppression(!lk.noiseSuppressionEnabled)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 12h3l3-7 4 14 3-7h5" />
                  {!lk.noiseSuppressionEnabled && <line x1="3" y1="3" x2="21" y2="21" />}
                </svg>
              </button>
              <span className="memeet-ctrl-label">{t("memeetNoiseLabel", "Gürültü engelleme")}</span>
            </div>

            <div className="memeet-ctrl">
              <button
                className={`icon-button large ${!isVideoEnabled ? "off" : "on"}`}
                onClick={handleToggleVideo}
                disabled={callStatus !== "in-call"}
                aria-pressed={isVideoEnabled}
                title={
                  isVideoEnabled
                    ? t("memeetTurnOffCamera", "Kamerayı kapat")
                    : t("memeetTurnOnCamera", "Kamerayı aç")
                }
              >
                {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
              </button>
              <span className={`memeet-ctrl-label ${!isVideoEnabled ? "off" : ""}`}>
                {isVideoEnabled ? t("memeetCameraOn", "Kamera açık") : t("memeetCameraOff", "Kamera kapalı")}
              </span>
            </div>

            <button
              className={`icon-button large ${isScreenSharing ? "active" : ""}`}
              onClick={toggleScreenShare}
              disabled={callStatus !== "in-call"}
              title={
                isScreenSharing
                  ? t("memeetStopSharing", "Paylaşımı durdur")
                  : t("memeetShareScreen", "Ekranı paylaş")
              }
            >
              <ScreenShareIcon />
            </button>

            {isScreenSharing && lk.localParticipant?.screenShareAudioTrack && (
              <button
                className={`icon-button ${isScreenAudioEnabled ? "active" : "off"}`}
                onClick={toggleScreenShareAudio}
                title={
                  isScreenAudioEnabled
                    ? t("memeetMuteScreenAudio", "Ekran sesini sustur")
                    : t("memeetUnmuteScreenAudio", "Ekran sesini aç")
                }
                aria-label={
                  isScreenAudioEnabled
                    ? t("memeetMuteScreenAudio", "Ekran sesini sustur")
                    : t("memeetUnmuteScreenAudio", "Ekran sesini aç")
                }
              >
                {isScreenAudioEnabled ? <MicIcon /> : <MicOffIcon />}
              </button>
            )}

            </div>

            <button className="icon-button large end" onClick={requestEndCall} title={t("memeetEndCall", "Aramayı bitir")}>
              <PhoneOffIcon />
            </button>
            </div>

            <div className="controls-right">
              <button
                className={`icon-button chat-toggle ${isChatOpen ? "active" : ""}`}
                onClick={toggleChat}
                title={isChatOpen ? t("memeetCloseChat", "Sohbeti kapat") : t("memeetOpenChat", "Sohbet")}
                aria-label={isChatOpen ? t("memeetCloseChat", "Sohbeti kapat") : t("memeetOpenChat", "Sohbet")}
                type="button"
              >
                <ChatIcon />
                {unreadChatCount > 0 && (
                  <span className="memeet-chat-badge">{unreadChatCount > 9 ? "9+" : unreadChatCount}</span>
                )}
              </button>
              <button
                className="icon-button device-settings-toggle"
                onClick={() => setIsDeviceSettingsOpen(true)}
                title={t("memeetSettings", "Ayarlar")}
                aria-label={t("memeetSettings", "Ayarlar")}
                type="button"
              >
                <SettingsIcon />
              </button>
              <button
                className={`icon-button soundboard-toggle ${isSoundboardOpen ? "active" : ""}`}
                onClick={toggleSoundboard}
                title={isSoundboardOpen ? t("memeetCloseSoundboard", "Kapat") : t("memeetSoundboard", "Soundboard")}
                type="button"
              >
                <SoundboardToggleIcon />
              </button>
              <span className="call-timer">{formatDuration(callDuration)}</span>
            </div>
          </div>
        </div>
      )}

      {isDeviceSettingsOpen && (
        <DeviceSettingsModal
          open={isDeviceSettingsOpen}
          onClose={() => setIsDeviceSettingsOpen(false)}
          t={t}
          audioInputId={selectedAudioInput}
          audioOutputId={selectedAudioOutput}
          videoInputId={selectedVideoInput}
          onSelectAudioInput={handleSelectAudioInput}
          onSelectAudioOutput={handleSelectAudioOutput}
          onSelectVideoInput={handleSelectVideoInput}
          shortcutsPanel={<ShortcutsPanel prefs={memeetPrefs} t={t} sounds={soundboardSounds} globalSupported={globalShortcutsSupported} />}
          videoExtra={<VideoEffectsPicker value={videoEffect} onChange={changeVideoEffect} t={t} busy={vfxBusy} compact />}
          audioExtra={
            <div className="device-settings-row">
              <label className="device-settings-label" htmlFor="device-settings-soundpad-vol">{t("memeetSoundpadVolume", "Efekt sesi")}</label>
              <input
                id="device-settings-soundpad-vol"
                type="range"
                min={0}
                max={100}
                step={5}
                value={Math.round(memeetPrefs.prefs.soundpadVolume * 100)}
                onChange={(e) => memeetPrefs.setSoundpadVolume(Number(e.target.value) / 100)}
              />
            </div>
          }
        />
      )}

      {chatContextMenu ? (
        <div
          className="memeet-chat-context-menu"
          style={{ left: chatContextMenu.x, top: chatContextMenu.y }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <button
            type="button"
            className="memeet-context-item"
            disabled={!canAddToChecklist || isChecklistAddInFlight}
            onClick={openAddToChecklistPicker}
          >
            {t("memeetAddToChecklist", "Add to checklist")}
          </button>
          <button
            type="button"
            className="memeet-context-item"
            disabled={!canAddToNotes || isNotesAddInFlight}
            onClick={async () => {
              const messageId = chatContextMenu.messageId;
              setChatContextMenu(null);
              await handleAddToNotes(messageId);
            }}
          >
            {isNotesAddInFlight ? t("memeetAdding", "Adding...") : t("memeetAddToNotes", "Add to notes")}
          </button>
        </div>
      ) : null}

      {isChecklistPickerOpen ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => closeAddToChecklistPicker()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="memeet-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="memeet-modal-title">{t("memeetAddToChecklist", "Add to checklist")}</h3>

            <div className="memeet-modal-body">
              <div className="memeet-modal-row">
                <div className="memeet-modal-label">{t("memeetWorkspace", "Workspace")}</div>
                <select
                  value={selectedChecklistWorkspaceId}
                  onChange={(e) => setSelectedChecklistWorkspaceId(e.target.value)}
                  disabled={isChecklistAddInFlight}
                >
                  {resolvedWorkspaces.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.title}
                    </option>
                  ))}
                </select>
              </div>

              {(() => {
                const msg = checklistPickerMessageId ? chatMessages.find((m) => m.id === checklistPickerMessageId) : null;
                const preview = msg ? truncateChatPreview(msg.text) : "";
                if (!preview) return null;
                return (
                  <div className="memeet-modal-preview" title={msg?.text || ""}>
                    {preview}
                  </div>
                );
              })()}

              {checklistActionError ? <div className="memeet-modal-error">{checklistActionError}</div> : null}
            </div>

            <div className="memeet-modal-actions">
              <button
                className="meet-button ghost"
                type="button"
                onClick={() => closeAddToChecklistPicker()}
                disabled={isChecklistAddInFlight}
              >
                {t("memeetCancel", "Cancel")}
              </button>
              <button
                className="meet-button primary"
                type="button"
                onClick={handleConfirmAddToChecklist}
                disabled={!selectedChecklistWorkspaceId || isChecklistAddInFlight || !canAddToChecklist}
              >
                {isChecklistAddInFlight ? t("memeetAdding", "Adding...") : t("memeetAdd", "Add")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isInviteModalOpen ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => {
            if (!isInviteSubmitting) closeInviteModal();
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            className="memeet-modal memeet-invite-modal"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form className="memeet-invite-form" onSubmit={handleInviteSubmit}>
              <div className="memeet-invite-header">
                <h3 className="memeet-modal-title">{t("memeetInvitePeople", "Invite People")}</h3>
                <button
                  type="button"
                  className="icon-button"
                  onClick={closeInviteModal}
                  disabled={isInviteSubmitting}
                  title={t("memeetClose", "Close")}
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="room-chip memeet-invite-room-chip" title={t("memeetMeetingLink", "Toplantı linki")}>
                <span className="room-chip-label">{t("memeetMeetingLink", "Meeting Link")}</span>
                <code className="room-chip-code">{sharePath}</code>
                <button className="chip-button" onClick={handleCopyRoomId} title={t("memeetCopy", "Kopyala")} type="button">
                  {isCopied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>

              <div className="memeet-invite-sections">
                {isRoomCreator ? (
                <>
                <section className="memeet-invite-section">
                  <div className="memeet-invite-section-title-row">
                    <h4>{t("memeetMyCollaborators", "My Collaborators")}</h4>
                    {selectedCollaboratorId ? (
                      <button
                        type="button"
                        className="memeet-inline-clear-btn"
                        onClick={() => setSelectedCollaboratorId("")}
                        disabled={isInviteSubmitting}
                      >
                        {t("memeetClearSelection", "Clear")}
                      </button>
                    ) : null}
                  </div>

                  {!canLoadCollaborators ? (
                    <p className="memeet-invite-hint">
                      {t("memeetCollaboratorsUnavailable", "Collaborators are not available.")}
                    </p>
                  ) : isCollaboratorsLoading ? (
                    <p className="memeet-invite-hint">{t("memeetLoading", "Loading...")}</p>
                  ) : collaborators.length === 0 ? (
                    <p className="memeet-invite-hint">{t("memeetNoCollaborators", "No collaborators found.")}</p>
                  ) : (
                    <>
                      <div className="memeet-invite-search-row">
                        <input
                          type="search"
                          className="memeet-invite-search-input"
                          value={collaboratorSearchQuery}
                          onChange={(e) => setCollaboratorSearchQuery(e.target.value)}
                          placeholder={t("memeetSearchCollaborators", "Search by name or email")}
                          disabled={isInviteSubmitting}
                        />
                        {collaboratorSearchTerm ? (
                          <button
                            type="button"
                            className="memeet-invite-search-clear"
                            onClick={() => setCollaboratorSearchQuery("")}
                            disabled={isInviteSubmitting}
                          >
                            {t("memeetClearSelection", "Clear")}
                          </button>
                        ) : null}
                      </div>

                      {filteredCollaborators.length === 0 ? (
                        <p className="memeet-invite-hint">
                          {t("memeetNoCollaboratorsMatchingSearch", "No collaborators match your search.")}
                        </p>
                      ) : (
                        <div className="memeet-invite-collaborators-list">
                          {filteredCollaborators.map((collaborator) => {
                            const userID = collaborator.id.startsWith("email:") ? "" : collaborator.id;
                            const invitedKey = makeInvitedCheckKey("collab", userID, collaborator.email);
                            const isInvited = Boolean(invitedStatusByKey[invitedKey]);
                            const isChecking = Boolean(isCheckingInvitedByKey[invitedKey]);
                            const isSelected = selectedCollaboratorId === collaborator.id;

                            return (
                              <button
                                key={collaborator.id}
                                type="button"
                                className={`memeet-invite-collaborator-item${isSelected ? " selected" : ""}`}
                                onClick={() => {
                                  setSelectedCollaboratorId(collaborator.id);
                                  setInviteActionError("");
                                }}
                                disabled={isInviteSubmitting}
                              >
                                <span className="memeet-invite-collaborator-main">
                                  <span className="memeet-invite-collaborator-name">{collaborator.name}</span>
                                  {collaborator.email ? (
                                    <span className="memeet-invite-collaborator-email">{collaborator.email}</span>
                                  ) : null}
                                </span>
                                <span className="memeet-invite-status-indicator">
                                  {isChecking ? (
                                    <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                  ) : isInvited ? (
                                    <InvitedCheckIcon />
                                  ) : null}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </section>

                <section className="memeet-invite-section">
                  <h4>{t("memeetInviteByEmail", "Invite by Email")}</h4>
                  <div className="memeet-invite-fields">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        setInviteActionError("");
                      }}
                      disabled={Boolean(selectedCollaboratorId) || isInviteSubmitting}
                      placeholder={t("memeetEmailPlaceholder", "email@example.com")}
                    />
                    <input
                      type="text"
                      value={inviteNameLastname}
                      onChange={(e) => {
                        setInviteNameLastname(e.target.value);
                        setInviteActionError("");
                      }}
                      disabled={Boolean(selectedCollaboratorId) || isInviteSubmitting}
                      placeholder={t("memeetNameLastnamePlaceholder", "Name Lastname")}
                    />
                  </div>
                  <p className="memeet-invite-hint">
                    {t(
                      "memeetInviteInputRuleHint",
                      "Use collaborator selection or email fields. If collaborator is selected, email fields are ignored."
                    )}
                  </p>
                </section>
                </>
                ) : null}

                <section className="memeet-invite-section memeet-invite-section--wide">
                  <h4>{t("memeetInvitedList", "Invited List")}</h4>
                  {!canLoadInvitations ? (
                    <p className="memeet-invite-hint">
                      {t("memeetInvitationsUnavailable", "Invitations are not available.")}
                    </p>
                  ) : isInvitationsLoading ? (
                    <p className="memeet-invite-hint">{t("memeetLoading", "Loading...")}</p>
                  ) : invitations.length === 0 ? (
                    <p className="memeet-invite-hint">{t("memeetNoInvitations", "No invitations yet.")}</p>
                  ) : (
                    <div className="memeet-invited-list">
                      {invitations.map((invitation) => {
                        const invitedKey = makeInvitedCheckKey(
                          "invite",
                          invitation.userID,
                          invitation.email
                        );
                        const isInvited = Boolean(invitedStatusByKey[invitedKey]);
                        const isChecking = Boolean(isCheckingInvitedByKey[invitedKey]);

                        return (
                          <div key={invitation.invitationCode} className="memeet-invited-row">
                            <div className="memeet-invited-main">
                              <span className="memeet-invited-name">
                                {displayInvitationName(invitation)}
                              </span>
                              <span className="memeet-invited-sub">
                                {invitation.email || invitation.userID || invitation.invitationCode}
                              </span>
                            </div>
                            <div className="memeet-invited-actions">
                              <span className="memeet-invite-status-indicator">
                                {isChecking ? (
                                  <span className="memeet-chat-action-spinner" aria-hidden="true" />
                                ) : isInvited ? (
                                  <InvitedCheckIcon />
                                ) : null}
                              </span>
                              {isRoomCreator ? (
                              <button
                                type="button"
                                className="meet-button ghost small"
                                onClick={() => setInvitationToRemove(invitation)}
                                disabled={!canRemoveInvitations || isRemovingInvitation}
                              >
                                {t("memeetCancelInvitation", "Cancel")}
                              </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>

              {isRoomCreator ? inviteActionError ? <div className="memeet-modal-error">{inviteActionError}</div> : null : null}
              {isRoomCreator ? inviteActionSuccess ? <div className="memeet-modal-success">{inviteActionSuccess}</div> : null : null}

              {/* Bildirim kanallari — secim KULLANICIYA ait. Hicbiri secilmezse
                  davet yine olusur, kullanici asagidaki baglantiyi kendi gonderir. */}
              {isRoomCreator ? (
                <div className="memeet-notify-channels">
                  <span className="memeet-notify-label">
                    {t("memeetNotifyVia", "Notify via")}
                  </span>
                  {([
                    ["notification", t("memeetNotifyInApp", "In-app notification")],
                    ["push", t("memeetNotifyPush", "Push notification")],
                    ["mail", t("memeetNotifyMail", "Email")],
                  ] as [string, string][]).map(([key, label]) => (
                    <label key={key} className="memeet-notify-option">
                      <input
                        type="checkbox"
                        checked={(notifyChannels as Record<string, boolean>)[key]}
                        onChange={(e) =>
                          setNotifyChannels((prev) => ({ ...prev, [key]: e.target.checked }))
                        }
                        disabled={isInviteSubmitting}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              ) : null}

              {inviteJoinLink ? (
                <div className="memeet-invite-link">
                  <input
                    className="memeet-invite-link-input"
                    type="text"
                    value={inviteJoinLink}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    className="meet-button ghost"
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard?.writeText(inviteJoinLink);
                        setInviteLinkCopied(true);
                        setTimeout(() => setInviteLinkCopied(false), 1600);
                      } catch {
                        /* pano erisimi yoksa kullanici elle secebilir */
                      }
                    }}
                  >
                    {inviteLinkCopied ? t("copied", "Copied") : t("copy", "Copy")}
                  </button>
                </div>
              ) : null}

              <div className="memeet-modal-actions">
                <button
                  className="meet-button ghost"
                  type="button"
                  onClick={closeInviteModal}
                  disabled={isInviteSubmitting}
                >
                  {t("memeetClose", "Close")}
                </button>
                {isRoomCreator ? (
                <button
                  className="meet-button primary"
                  type="submit"
                  disabled={!canInviteUsers || isInviteSubmitting}
                >
                  {isInviteSubmitting ? t("memeetSendingInvitation", "Sending...") : t("memeetSendInvite", "Send Invite")}
                </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {invitationToRemove ? (
        <div
          className="memeet-modal-backdrop"
          onMouseDown={() => {
            if (!isRemovingInvitation) setInvitationToRemove(null);
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="memeet-modal memeet-invite-confirm-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="memeet-modal-title">{t("memeetCancelInvitation", "Cancel Invitation")}</h3>
            <div className="memeet-modal-body">
              <p className="memeet-invite-hint">
                {t(
                  "memeetCancelInvitationConfirm",
                  "Are you sure you want to cancel this invitation?"
                )}
              </p>
              <div className="memeet-modal-preview">
                {displayInvitationName(invitationToRemove)}
              </div>
            </div>
            <div className="memeet-modal-actions">
              <button
                className="meet-button ghost"
                type="button"
                onClick={() => setInvitationToRemove(null)}
                disabled={isRemovingInvitation}
              >
                {t("memeetClose", "Close")}
              </button>
              <button
                className="meet-button danger"
                type="button"
                onClick={() => void handleConfirmRemoveInvitation()}
                disabled={isRemovingInvitation || !canRemoveInvitations}
              >
                {isRemovingInvitation ? t("memeetRemoving", "Removing...") : t("memeetConfirm", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lightboxImage && createPortal(
        <div className="memeet-lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="memeet-lightbox-close" onClick={() => setLightboxImage(null)}>
            <CloseIcon />
          </button>
          <img src={lightboxImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
    </div>
  );
}

function pickLikelyScreenTrack(videoTracks: MediaStreamTrack[]): MediaStreamTrack | null {
  if (!Array.isArray(videoTracks) || videoTracks.length === 0) return null;

  const keywordPattern =
    /\b(screen|display|window|monitor|tab|capture|share|ekran|paylas|paylasim|paylaş|paylaşım)\b/i;
  const byLabel = videoTracks.find((track) => keywordPattern.test(String(track.label || "")));
  if (byLabel) return byLabel;

  const liveTracks = videoTracks.filter((track) => track.readyState === "live");
  if (liveTracks.length === 1) return liveTracks[0];

  return videoTracks[videoTracks.length - 1] || null;
}

function AvatarCircle({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  if (avatarUrl) {
    return <img className="avatar-image" src={avatarUrl} alt={name} />;
  }

  return <span className="avatar-initials">{getInitials(name)}</span>;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join("");
  return initials.toUpperCase() || "U";
}

function PrejoinHostIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" />
    </svg>
  );
}

const IDENTITY_MARKER = "|memeet:v1:";

function encodeIdentityNickname(displayName: string, avatarUrl?: string, lodosUserId?: string): string {
  const safeName = (displayName || "").trim() || "User";
  const safeAvatar = (avatarUrl || "").trim();
  const safeUserId = String(lodosUserId || "").trim();

  const payloadObj: { a?: string; u?: string } = {};
  if (safeAvatar) payloadObj.a = safeAvatar;
  if (safeUserId) payloadObj.u = safeUserId;
  if (!payloadObj.a && !payloadObj.u) return safeName;

  const payload = JSON.stringify(payloadObj);
  return `${safeName} ${IDENTITY_MARKER}${base64UrlEncode(payload)}`;
}

function parseIdentityNickname(rawNickname: string): { displayName: string; avatarUrl?: string; lodosUserId?: string } {
  const raw = (rawNickname || "").trim();
  const idx = raw.indexOf(IDENTITY_MARKER);
  if (idx === -1) return { displayName: raw || "User" };

  const displayName = raw.slice(0, idx).trim() || "User";
  const encoded = raw.slice(idx + IDENTITY_MARKER.length).trim();

  try {
    const decoded = base64UrlDecode(encoded);
    const parsed = JSON.parse(decoded) as { a?: unknown; u?: unknown };
    const avatar = typeof parsed?.a === "string" ? parsed.a : undefined;
    const userId = typeof parsed?.u === "string" ? parsed.u : undefined;
    return { displayName, avatarUrl: avatar, lodosUserId: userId };
  } catch {
    return { displayName };
  }
}

function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(input: string): string {
  let base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  if (pad) base64 += "=".repeat(4 - pad);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

function makeChatId(): string {
  const cryptoObj = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof cryptoObj?.randomUUID === "function") {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // ignore
    }
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function truncateChatPreview(text: string, maxLength = 72): string {
  const compact = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function truncateChatActionToken(text: string, maxChars = 6): string {
  const compact = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!compact) return "";
  const chars = Array.from(compact);
  if (chars.length <= maxChars) return compact;
  return `${chars.slice(0, maxChars).join("")}...`;
}

function decodeRtcData(data: unknown): string | null {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  if (ArrayBuffer.isView(data)) return new TextDecoder().decode(data);
  return null;
}

const MEMEET_REC_CSS = `
.memeet-rec-badge { display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px; border-radius: 999px;
  background: rgba(40,12,12,0.82); border: 1px solid rgba(255,72,72,0.5); color: #ffd9d9;
  font-size: 12px; font-weight: 700; letter-spacing: .2px; backdrop-filter: blur(8px);
  white-space: nowrap; flex: 0 0 auto; height: 32px; box-sizing: border-box; }
.memeet-rec-dot { width: 9px; height: 9px; border-radius: 50%; background: #ff4848;
  box-shadow: 0 0 0 0 rgba(255,72,72,0.6); animation: memeetRecPulse 1.6s infinite; }
@keyframes memeetRecPulse { 0% { box-shadow: 0 0 0 0 rgba(255,72,72,0.55); } 70% { box-shadow: 0 0 0 8px rgba(255,72,72,0); } 100% { box-shadow: 0 0 0 0 rgba(255,72,72,0); } }
.memeet-rec-toast { position: fixed; top: 56px; left: 50%; transform: translateX(-50%); z-index: 2147482000;
  max-width: 420px; padding: 11px 18px; border-radius: 12px; font-size: 13px; font-weight: 600; line-height: 1.4;
  box-shadow: 0 14px 40px rgba(0,0,0,0.5); animation: memeetRecToastIn .28s ease both; }
.memeet-rec-toast.on { background: #2a1414; color: #ffd0d0; border: 1px solid rgba(255,72,72,0.55); }
.memeet-rec-toast.off { background: #1b2330; color: #cdd8e6; border: 1px solid rgba(160,180,210,0.35); }
@keyframes memeetRecToastIn { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }
.memeet-mute-toast { position: fixed; top: 102px; left: 50%; transform: translateX(-50%); z-index: 2147482000;
  max-width: 420px; padding: 11px 18px; border-radius: 12px; font-size: 13px; font-weight: 600; line-height: 1.4;
  box-shadow: 0 14px 40px rgba(0,0,0,0.5); animation: memeetRecToastIn .28s ease both; }
.memeet-mute-toast.info { background: #14202a; color: #cdeafe; border: 1px solid rgba(56,189,248,0.5); }
.memeet-mute-toast.error { background: #2a1414; color: #ffd0d0; border: 1px solid rgba(255,72,72,0.55); }
.memeet-vidrec-btn { display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 700; letter-spacing: .2px; cursor: pointer; white-space: nowrap; flex: 0 0 auto;
  height: 32px; box-sizing: border-box; transition: background .15s, border-color .15s, filter .15s; }
.memeet-vidrec-btn:disabled { opacity: .55; cursor: default; }
.memeet-vidrec-btn.start { background: rgba(255,72,72,0.14); border: 1px solid rgba(255,72,72,0.5); color: #ffd9d9; }
.memeet-vidrec-btn.start:hover:not(:disabled) { background: rgba(255,72,72,0.24); }
.memeet-vidrec-btn.stop { background: rgba(40,12,12,0.85); border: 1px solid rgba(255,72,72,0.7); color: #ffe3e3; }
.memeet-vidrec-btn.stop:hover:not(:disabled) { filter: brightness(1.12); }
.memeet-vidrec-indicator { position: fixed; top: 14px; left: 50%; transform: translateX(-50%); z-index: 2147482050;
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px;
  background: rgba(40,12,12,0.9); border: 1px solid rgba(255,72,72,0.6); color: #ffdede;
  font-size: 13px; font-weight: 700; letter-spacing: .2px; backdrop-filter: blur(8px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.45); animation: memeetRecToastIn .28s ease both; }
.memeet-vidrec-indicator-dot { width: 10px; height: 10px; border-radius: 50%; background: #ff4848;
  box-shadow: 0 0 0 0 rgba(255,72,72,0.6); animation: memeetRecPulse 1.6s infinite; }
.memeet-vidrec-consent-backdrop { position: fixed; inset: 0; background: rgba(4,7,12,0.62); backdrop-filter: blur(3px);
  z-index: 2147483300; display: flex; align-items: center; justify-content: center; padding: 20px; }
.memeet-vidrec-consent { width: min(420px, 94vw); display: flex; flex-direction: column; align-items: center; gap: 16px;
  background: #11161e; color: #eaf0f6; border: 1px solid rgba(255,255,255,0.12); border-radius: 18px; padding: 26px 24px;
  box-shadow: 0 24px 70px rgba(0,0,0,0.65); text-align: center; animation: memeetRecToastIn .25s ease both; }
.memeet-vidrec-consent-ico { width: 46px; height: 46px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center;
  background: rgba(255,72,72,0.14); color: #ff8a8a; border: 1px solid rgba(255,72,72,0.3); }
.memeet-vidrec-consent-text { font-size: 14px; line-height: 1.55; margin: 0; }
.memeet-vidrec-consent-actions { display: flex; gap: 10px; width: 100%; }
.memeet-vidrec-consent-cancel, .memeet-vidrec-consent-confirm { flex: 1; padding: 11px 16px; border-radius: 11px; font-size: 13.5px; font-weight: 700; cursor: pointer; }
.memeet-vidrec-consent-cancel { background: rgba(255,255,255,0.06); color: #eaf0f6; border: 1px solid rgba(255,255,255,0.16); }
.memeet-vidrec-consent-cancel:hover { background: rgba(255,255,255,0.12); }
.memeet-vidrec-consent-confirm { background: #e5484d; color: #fff; border: 1px solid transparent; }
.memeet-vidrec-consent-confirm:hover:not(:disabled) { filter: brightness(1.08); }
.memeet-vidrec-consent-confirm:disabled { opacity: .6; cursor: default; }
@media (max-width: 720px) {
  .memeet-vidrec-btn { padding: 0; width: 38px; height: 38px; justify-content: center; }
  .memeet-vidrec-btn span { display: none; }
  .memeet-rec-badge { padding: 6px 9px; font-size: 11px; height: 34px; }
}
`;

function parseRecordingStateWireV1(raw: string): { on: boolean; fromName?: string } | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "recording-state" || obj.v !== 1) return null;
    const on = typeof obj.on === "boolean" ? obj.on : null;
    if (on === null) return null;
    const fromName = typeof obj.fromName === "string" ? obj.fromName : undefined;
    return { on, fromName };
  } catch {
    return null;
  }
}

function parseReactionWireV1(raw: string): { kind: ReactionKind; fromName?: string } | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "reaction" || obj.v !== 1) return null;
    const kind = typeof obj.kind === "string" ? obj.kind : "";
    // Bilinmeyen tur YOK SAYILIR: ileride yeni tepki eklenirse eski istemci
    // bos kutu cizmek yerine sessiz kalir.
    if (!REACTION_KINDS.some((r) => r.kind === kind)) return null;
    const fromName = typeof obj.fromName === "string" ? obj.fromName : undefined;
    return { kind: kind as ReactionKind, fromName };
  } catch {
    return null;
  }
}

function parseHandStateWireV1(raw: string): { raised: boolean; fromName?: string; ts?: number } | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "hand-state" || obj.v !== 1) return null;
    const raised = typeof obj.raised === "boolean" ? obj.raised : null;
    if (raised === null) return null;
    const fromName = typeof obj.fromName === "string" ? obj.fromName : undefined;
    const ts = typeof obj.ts === "number" && Number.isFinite(obj.ts) ? obj.ts : undefined; // [MEMEET-HANDORDER]
    return { raised, fromName, ts };
  } catch {
    return null;
  }
}

function parseChatWireChecklistAddedV1(raw: string): ChatWireChecklistAddedV1 | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "chat-message" || obj.v !== 1) return null;

    const meta = obj.meta && typeof obj.meta === "object" ? (obj.meta as Record<string, unknown>) : null;
    if (!meta || meta.kind !== "checklist-added") return null;

    const messageId = typeof meta.messageId === "string" ? meta.messageId : null;
    const workspaceId = typeof meta.workspaceId === "string" ? meta.workspaceId : null;
    const checklistName = typeof meta.checklistName === "string" ? meta.checklistName : null;
    if (!messageId || !workspaceId || !checklistName) return null;

    const ts = typeof obj.ts === "number" ? obj.ts : Date.now();
    const checklistId = typeof meta.checklistId === "string" ? meta.checklistId : undefined;
    const byUserId = typeof meta.byUserId === "string" ? meta.byUserId : undefined;
    const byName = typeof meta.byName === "string" ? meta.byName : undefined;
    const preview = typeof meta.preview === "string" ? meta.preview : undefined;

    return {
      v: 1,
      type: "chat-checklist-added",
      ts,
      messageId,
      workspaceId,
      checklistName,
      checklistId,
      byUserId,
      byName,
      preview,
    };
  } catch {
    return null;
  }
}

function parseSoundboardWireMessageV1(raw: string): SoundboardWireMessageV1 | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "soundboard-play" || obj.v !== 1) return null;

    const soundId = typeof obj.soundId === "string" ? obj.soundId : null;
    if (!soundId) return null;

    return {
      v: 1,
      type: "soundboard-play",
      soundId,
      fromName: typeof obj.fromName === "string" ? obj.fromName : "",
      ts: typeof obj.ts === "number" ? obj.ts : Date.now(),
    };
  } catch {
    return null;
  }
}

function parseChatWireMessageV1(raw: string): { id: string; ts: number; text: string; fromName?: string; fromAvatarUrl?: string; attachment?: ChatAttachment } | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const obj = parsed as Record<string, unknown>;
    if (obj.type !== "chat-message" || obj.v !== 1) return null;

    const id = typeof obj.id === "string" ? obj.id : null;
    const ts = typeof obj.ts === "number" ? obj.ts : null;
    const text = typeof obj.text === "string" ? obj.text : "";
    if (!id) return null;

    const from = obj.from && typeof obj.from === "object" ? (obj.from as Record<string, unknown>) : null;
    const fromName = from && typeof from.name === "string" ? from.name : undefined;
    const fromAvatarUrl = from && typeof from.avatarUrl === "string" ? from.avatarUrl : undefined;

    let attachment: ChatAttachment | undefined;
    if (obj.attachment && typeof obj.attachment === "object") {
      const att = obj.attachment as Record<string, unknown>;
      if (
        typeof att.type === "string" &&
        typeof att.name === "string" &&
        typeof att.data === "string" &&
        typeof att.size === "number"
      ) {
        attachment = {
          type: att.type,
          name: att.name,
          data: att.data,
          size: att.size,
        };
      }
    }

    return { id, ts: ts ?? Date.now(), text, fromName, fromAvatarUrl, attachment };
  } catch {
    return null;
  }
}

function AttachmentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function VideoOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function HandRaiseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 13V6.5a1.5 1.5 0 0 1 3 0V12" />
      <path d="M11 12V4.5a1.5 1.5 0 0 1 3 0V12" />
      <path d="M14 12.5V6.5a1.5 1.5 0 0 1 3 0V13" />
      <path d="M17 13v-3a1.5 1.5 0 0 1 3 0v6c0 3.31-2.69 6-6 6h-1.5a6 6 0 0 1-5.4-3.4L6 15.5c-.5-1-.2-2.2.7-2.8.8-.5 1.9-.3 2.5.5l.8 1.1" />
    </svg>
  );
}

function ScreenShareIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function PiPIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <rect x="12" y="9" width="8" height="6" rx="1" ry="1" fill="currentColor" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.87-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.75-1.68-1.38-2.66-1.87-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
    </svg>
  );
}

function InviteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function RecDotIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}

function RecStopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

function InvitedCheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.8 2.8L16.5 9.8" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChecklistAddIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h7" />
      <path d="M18 17h5" />
      <path d="M20.5 14.5v5" />
    </svg>
  );
}

function NotesAddIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12V8z" />
      <path d="M14 2v6h6" />
      <path d="M18 17h5" />
      <path d="M20.5 14.5v5" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4.5h16a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-7l-3.5 3.5v-3.5H4A1.5 1.5 0 0 1 2.5 16V6A1.5 1.5 0 0 1 4 4.5z" />
      <circle cx="9" cy="11" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="11" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="15" cy="11" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SoundboardToggleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function SoundboardIcon({ icon }: { icon: string }) {
  switch (icon) {
    case "clap":
      return <span className="soundboard-emoji">👏</span>;
    case "clap-single":
      return <span className="soundboard-emoji">👋</span>;
    case "ovation":
      return <span className="soundboard-emoji">🎉</span>;
    case "laugh":
      return <span className="soundboard-emoji">😂</span>;
    case "mock":
      return <span className="soundboard-emoji">🤣</span>;
    case "news":
      return <span className="soundboard-emoji">📰</span>;
    case "cricket":
      return <span className="soundboard-emoji">🦗</span>;
    case "ahh":
      return <span className="soundboard-emoji">😮</span>;
    case "choir":
      return <span className="soundboard-emoji">😇</span>;
    case "man-ahh":
      return <span className="soundboard-emoji">🗣️</span>;
    case "sad":
      return <span className="soundboard-emoji">😢</span>;
    case "wow":
      return <span className="soundboard-emoji">🤩</span>;
    default:
      return <span className="soundboard-emoji">🔊</span>;
  }
}

function SpeakerCtrlIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" />
    </svg>
  );
}

function SpeakerOffCtrlIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <line x1="22" y1="9" x2="16" y2="15" />
      <line x1="16" y1="9" x2="22" y2="15" />
    </svg>
  );
}
